import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildCountryList, buildCountryMap, ISO_CODE_REGEX } from '../../shared/countryUtils';
import countryAliasMap from '../../shared/countryAliasMap.json';
import { US_STATES, STATE_NAME_TO_CODE, buildStateList, buildStateMap } from '../../shared/usStatesData';
import { useAuth } from '../contexts/AuthContext';
import RecipeBuilder from './recipes/RecipeBuilder';
import { extractSharedFieldsForInventory, extractMenuManagerOnlyFields, getSheetKeyFromCategory } from '../../utils/menuInventorySync';
import { isCloudinaryUrl } from '../../utils/cloudinaryUtils';


 const isProbablyIOS = () => {
   if (typeof navigator === 'undefined') return false;
   const ua = navigator.userAgent || '';
   const platform = navigator.platform || '';
   const isIOSDevice = /iPad|iPhone|iPod/i.test(ua);
   const isIPadOS = platform === 'MacIntel' && Number(navigator.maxTouchPoints || 0) > 1;
   return isIOSDevice || isIPadOS;
 };

const getCodeFromAttributes = (pathEl) => {
  if (!pathEl) return null;
  const idAttr = pathEl.getAttribute('id');
  if (idAttr && ISO_CODE_REGEX.test(idAttr)) {
    return idAttr.toUpperCase();
  }
  const nameAttr = pathEl.getAttribute('name');
  if (nameAttr && countryAliasMap[nameAttr]) {
    return countryAliasMap[nameAttr];
  }
  const classAttr = pathEl.getAttribute('class');
  if (classAttr && countryAliasMap[classAttr]) {
    return countryAliasMap[classAttr];
  }
  return null;
};

// Map container — React owns the SVG content via dangerouslySetInnerHTML,
// useLayoutEffect configures it synchronously before paint.
const MapContainer = ({ mapSvgContent, mapError, mapRef, svgRef, onMapReady, mapType }) => {
  const onMapReadyRef = useRef(onMapReady);

  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);

  // Configure the SVG synchronously after React injects it via dangerouslySetInnerHTML.
  // useLayoutEffect runs before the browser paints, preventing any flash of unstyled SVG.
  useLayoutEffect(() => {
    if (!mapRef.current || !mapSvgContent) return;

    const svg = mapRef.current.querySelector('svg');
    if (!svg) return;

    // worldmap.svg has no viewBox → set it. Blank_US_Map.svg already has one → leave it.
    if (!svg.getAttribute('viewBox')) {
      svg.setAttribute('viewBox', '0 0 2000 857');
    }
    svg.setAttribute('preserveAspectRatio', 'xMidYMin meet');
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.style.width = '100%';
    svg.style.height = 'auto';
    svg.style.display = 'block';
    svg.setAttribute('stroke', '#ececec');

    if (svgRef) svgRef.current = svg;

    // Process paths in batches asynchronously (non-blocking)
    const allPaths = svg.querySelectorAll('path');
    const batchSize = 200;

    const processBatch = (startIndex) => {
      const endIndex = Math.min(startIndex + batchSize, allPaths.length);
      for (let i = startIndex; i < endIndex; i++) {
        const path = allPaths[i];
        path.setAttribute('stroke', '#d0d0d0');
        path.setAttribute('stroke-width', '0.4');
        const code = getCodeFromAttributes(path);
        if (code) {
          path.dataset.code = code;
          path.style.cursor = 'pointer';
          path.style.transition = 'fill 0.15s ease';
          path.style.setProperty('fill', '#d0d0d0', 'important');
          path.style.setProperty('stroke', '#d0d0d0', 'important');
        }
      }
      if (endIndex < allPaths.length) {
        requestAnimationFrame(() => processBatch(endIndex));
      } else {
        if (onMapReadyRef.current) onMapReadyRef.current();
      }
    };

    requestAnimationFrame(() => {
      if (mapRef.current) processBatch(0);
    });
  }, [mapSvgContent, mapRef, svgRef]);

  return (
    <div
      ref={mapRef}
      dangerouslySetInnerHTML={{ __html: mapError ? '' : (mapSvgContent || '') }}
      style={{ width: '100%', minHeight: '260px', borderRadius: '8px', paddingTop: 0, paddingRight: '40px', paddingBottom: 0, paddingLeft: 0, background: 'transparent', position: 'relative', marginLeft: '-60px', overflow: 'hidden' }}
    />
  );
};

// Wrap in memo to prevent re-renders when parent re-renders
// The component itself can re-render, but the SVG inside is protected by svgMountedRef
const MemoizedMapContainer = memo(MapContainer, (prevProps, nextProps) => {
  // Re-render when mapType or mapSvgContent changes
  if (prevProps.mapType !== nextProps.mapType) return false;
  if (prevProps.mapSvgContent !== nextProps.mapSvgContent) return false;
  // Otherwise, don't re-render - the SVG is protected by the svgMountedRef check inside
  return true;
});

MemoizedMapContainer.displayName = 'MapContainer';

/**
 * Hook to measure container size (based on menugallery2.js useContainerSize)
 */
function useContainerSize(containerRef) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };

    updateSize();

    if (window.ResizeObserver) {
      const observer = new ResizeObserver(() => {
        updateSize();
      });
      observer.observe(node);
      return () => observer.disconnect();
    } else {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }
  }, [containerRef]);

  return size;
}

/**
 * Video background component for viewer (based on menugallery2.js VideoBackground)
 */
function VideoBackground({ videoSrc, videoRef, onLoadedData, onError, API_BASE_URL, currentCocktail, videoPreviewUrl, isProcessing, videoScale = 1, videoOffsetX = null }) {
  const shouldRender = isCloudinaryUrl(videoSrc) && !isProcessing;

  // Compose the transform so translate is applied in the parent's coordinate space
  // (listing it before scale keeps the offset a true distance, unmultiplied by scale).
  const videoTransform = [
    videoOffsetX ? `translateX(${videoOffsetX})` : null,
    videoScale !== 1 ? `scale(${videoScale})` : null,
  ].filter(Boolean).join(' ') || undefined;

  useEffect(() => {
    if (!shouldRender) return;
    const video = videoRef?.current;
    if (!video) return;

    const playVideo = () => {
      if (video.paused) {
        video.play().catch(() => {});
      }
    };

    const resumeOnGesture = () => {
      playVideo();
    };

    video.addEventListener('canplay', playVideo);
    video.addEventListener('loadeddata', playVideo);
    document.addEventListener('touchstart', resumeOnGesture, { passive: true, once: true });
    document.addEventListener('click', resumeOnGesture, { passive: true, once: true });

    playVideo();

    return () => {
      video.removeEventListener('canplay', playVideo);
      video.removeEventListener('loadeddata', playVideo);
      document.removeEventListener('touchstart', resumeOnGesture);
      document.removeEventListener('click', resumeOnGesture);
    };
  }, [shouldRender, videoSrc, videoRef]);

  if (!shouldRender) {
    return null;
  }

  return (
    <video
      key={videoSrc}
      ref={videoRef}
      autoPlay
      muted
      loop
      playsInline
      webkit-playsinline="true"
      preload="auto"
      crossOrigin={isProbablyIOS() ? undefined : 'anonymous'}
      onError={onError}
      onLoadedData={onLoadedData}
      onLoadedMetadata={() => {
        if (videoRef?.current) {
          const video = videoRef.current;
        }
      }}
      onCanPlay={() => {
        if (videoRef?.current && videoRef.current.paused) {
          videoRef.current.play().catch(() => {});
        }
      }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
        pointerEvents: 'none',
        zIndex: 0, // Video should be at the base layer
        transform: videoTransform,
        transformOrigin: 'center',
      }}
    >
      <source src={videoSrc} type="video/mp4" />
    </video>
  );
}

// Determine map type from cocktail data.
// Priority: 1) explicit mapType from inventory/DB, 2) location-based detection, 3) default 'world'.
// Uses every() not some() — only infers 'us' when ALL saved regions are US state codes.
// This avoids false positives for world cocktails that include one code that happens to overlap
// with a US state abbreviation (e.g. CA=Canada vs California). A world cocktail will almost
// always have at least one region code that isn't a US state (FR, GB, JP, IT, etc.).
// Base-spirit toggles shown above Ingredients on Social Tonics and CLASSICS.
// Stored per item as an array in the inventory row's hidden baseSpirits column.
const BASE_SPIRIT_OPTIONS = ['VODKA', 'TEQUILA', 'GIN', 'BOURBON', 'SCOTCH', 'RUM', 'NA'];

const US_STATE_CODES = new Set(US_STATES.map(s => s.code.toUpperCase()));
const inferMapType = (cocktail) => {
  const explicit = cocktail?.mapType;
  if (explicit === 'us' || explicit === 'world') return explicit;
  if (Array.isArray(cocktail?.regions) && cocktail.regions.length > 0) {
    if (cocktail.regions.every(r => US_STATE_CODES.has(String(r).toUpperCase()))) return 'us';
  }
  return 'world';
};

// Pure category helpers — module scope so callbacks that reference them (navigateToCocktail,
// the recipe fetch) can keep honest dependency arrays instead of closing over a new function
// identity on every render.
const normalizeCategoryKey = (value = '') => {
  const key = String(value).toLowerCase();
  // Map old category names to new ones for backward compatibility
  const categoryMap = {
    'classics': 'cocktails',
    'originals': 'mocktails'
  };
  return categoryMap[key] || key;
};

// Get recipe type from category
const getRecipeType = (category) => {
  const cat = normalizeCategoryKey(category);
  if (cat === 'cocktails') return 'cocktail';
  if (cat === 'mocktails') return 'mocktail';
  if (cat === 'premix') return 'premix';
  return null;
};

// Check if category should show recipe builder
const shouldShowRecipeBuilder = (category) => Boolean(getRecipeType(category));

// Create blank recipe
const createBlankRecipe = (type) => ({
  title: '',
  type: type,
  items: [],
  totals: { volumeOz: 0, costEach: 0 },
  metadata: {},
  backgroundColor: '#e5e5e5',
  batch: { size: 0, unit: 'ml', yieldCount: 0 }
});

const MenuManager = () => {
  const { apiCall, isAuthenticated, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
  // Video uploads are only offered when running locally — the hosted admin has no
  // local worker to process the file.
  const isLocalhost = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const host = String(window.location?.hostname || '');
    return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host.endsWith('.localhost');
  }, []);

  const isRenderSite = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const host = String(window.location?.hostname || '');
    const base = String(API_BASE_URL || '');
    return host.includes('onrender.com') || base.includes('onrender.com');
  }, [API_BASE_URL]);
  const [cocktails, setCocktails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('cocktails');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editingCocktail, setEditingCocktail] = useState(null);
  const [unsavedChangesModal, setUnsavedChangesModal] = useState({ show: false, direction: null });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const prevEditingCocktailIdRef = useRef(null);
  const activeCocktailIdRef = useRef(null);
  const isNavigatingRef = useRef(false); // Prevents useEffect from overwriting during navigation
  const navVersionRef = useRef(0); // Increments on every navigation — async results check this before applying
  const recipeRef = useRef(null);
  const lastRecipeHydrateAtRef = useRef(0);
  const recipeBuilderInteractedRef = useRef(false);
  const recipeRequestIdRef = useRef(0);
  const preserveRecipeViewRef = useRef(false); // Set by arrows so recipe view survives navigation
  const recipeForCocktailIdRef = useRef(null); // Tracks which cocktail ID the current recipe belongs to
  const [newCocktail, setNewCocktail] = useState({
    name: '',
    concept: '',
    ingredients: '',
    globalIngredients: '',
    // garnish comes from RecipeBuilder (recipe.metadata.garnish), not from cocktail
    category: 'cocktails',
    page: 'cocktails',
    videoFile: '',
    order: 0,
    regions: []
  });
  const [videoUpload, setVideoUpload] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [processingStatus, setProcessingStatus] = useState(null); // { active, stage, progress, total, message, error } for current item
  const processingPollIntervalRef = useRef(null);
  const [workerStatus, setWorkerStatus] = useState({
    online: false,
    configured: false,
    workerId: null,
    lastSeenSecondsAgo: null,
  });
  const [videoOptionsModal, setVideoOptionsModal] = useState({
    show: false,
    file: null,
    itemNumber: null
  });
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info' // 'info', 'success', 'warning', 'error'
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const viewerContainerRef = useRef(null);
  const saveButtonRef = useRef(null);
  const headerRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const viewerSize = useContainerSize(viewerContainerRef);
  const [countries, setCountries] = useState([]);
  const [countryQuery, setCountryQuery] = useState('');
  const [mapType, setMapType] = useState('world'); // 'world' or 'us'
  // View toggle: off = current view (map/countries panel hidden on Social Tonics / CLASSICS),
  // on = legacy view (panel restored). Purely a view preference, not saved to the item.
  const [legacyView, setLegacyView] = useState(false);
  const mapRef = useRef(null);
  const svgRef = useRef(null); // Direct ref to the SVG element
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapSvgContent, setMapSvgContent] = useState('');
  const [mapError, setMapError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const fetchInProgressRef = useRef(false);
  const savedCocktailIdRef = useRef(null);
  const savedCategoryRef = useRef(null);
  const [recipe, setRecipe] = useState(null);
  // Mirrors recipeForCocktailIdRef as state so the render can refuse to draw a recipe that
  // doesn't belong to the item on screen. A ref alone can't gate rendering.
  const [recipeOwnerId, setRecipeOwnerId] = useState(null);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [savingRecipe, setSavingRecipe] = useState(false);
  const [recipeViewActive, setRecipeViewActive] = useState(false);

  // Single choke point for every write to `recipe`: the recipe and the id of the cocktail it
  // belongs to are set together, so they can never drift apart. recipeRef is updated
  // synchronously here because the guards below read it during the same tick.
  const applyRecipe = useCallback((nextRecipe, ownerId, { onlyWhenPresent = false } = {}) => {
    if (onlyWhenPresent && recipeRef.current === null) return;
    recipeRef.current = nextRecipe;
    recipeForCocktailIdRef.current = ownerId || null;
    setRecipeOwnerId(ownerId || null);
    setRecipe(nextRecipe);
  }, []);

  const clearRecipe = useCallback(() => {
    recipeRef.current = null;
    recipeForCocktailIdRef.current = null;
    setRecipeOwnerId(null);
    setRecipe(null);
  }, []);

  useEffect(() => {
    return () => {
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [videoPreviewUrl]);

  // Poll local worker availability (Render backend reports heartbeats)
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await apiCall('/video-worker/status');
        if (!cancelled && status && typeof status === 'object') {
          setWorkerStatus((prev) => ({ ...prev, ...status }));
        }
      } catch (err) {
        if (!cancelled) {
          setWorkerStatus((prev) => ({ ...prev, online: false }));
        }
      }
    };

    tick();
    const interval = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [apiCall]);
  
  // Handle URL parameters to navigate to specific item
  useEffect(() => {
    const urlCategory = searchParams.get('category');
    const urlItemNumber = searchParams.get('itemNumber');
    const urlItemId = searchParams.get('itemId'); // Legacy support
    
    if (urlCategory && cocktails.length > 0) {
      const categoryKey = normalizeCategoryKey(urlCategory);
      setSelectedCategory(categoryKey);
      
      // Filter cocktails the same way as filteredCocktails to get correct index
      const categoryFiltered = cocktails.filter(c => {
        const cat = normalizeCategoryKey(c.category);
        if (categoryKey === 'archived') {
          return c.status === 'archived';
        }
        const isActive = c.status !== 'archived' && (c.isActive !== false);
        const categoryMatches = cat === categoryKey;
        return isActive && categoryMatches;
      });
      
      let targetIndex = -1;
      const urlItemNumberNum = Number(urlItemNumber);
      if (urlItemNumber && Number.isFinite(urlItemNumberNum)) {
        // Find by itemNumber (most reliable)
        targetIndex = categoryFiltered.findIndex(c => 
          Number(c.itemNumber) === urlItemNumberNum
        );
      } else if (urlItemId) {
        // Fallback: find by itemId or name (legacy support)
        targetIndex = categoryFiltered.findIndex(c => 
          c.itemId === urlItemId || 
          c.name?.toLowerCase() === decodeURIComponent(urlItemId).toLowerCase()
        );
      }
      
      if (targetIndex !== -1) {
        setCurrentIndex(targetIndex);
        // Clear URL params after navigation
        setSearchParams({});
      }
    }
  }, [cocktails, searchParams, setSearchParams]);

  // Update currentIndex to show saved cocktail after list refreshes
  useEffect(() => {
    if (savedCocktailIdRef.current && cocktails.length > 0) {
      const categoryKey = savedCategoryRef.current || selectedCategory;
      const categoryFiltered = cocktails.filter(c => {
        const cat = normalizeCategoryKey(c.category);
        return cat === categoryKey;
      });
      const savedIndex = categoryFiltered.findIndex(c => 
        (c._id === savedCocktailIdRef.current) || (c.itemId === savedCocktailIdRef.current)
      );
      if (savedIndex !== -1) {
        setCurrentIndex(savedIndex);
        savedCocktailIdRef.current = null; // Reset after finding
        savedCategoryRef.current = null;
      }
    }
  }, [cocktails, selectedCategory]);

  // Menu categories configuration - ordered as specified: COCKTAILS, MOCKTAILS, BEER, WINE, SPIRITS, PRE-MIX
  const menuCategories = [
    { key: 'cocktails', label: 'SOCIAL TONICS', icon: 'classics' },
    { key: 'mocktails', label: 'CLASSICS', icon: 'originals' },
    { key: 'beer', label: 'BEER', icon: 'spirits' },
    { key: 'wine', label: 'WINE', icon: 'spirits' },
    { key: 'spirits', label: 'SPIRITS', icon: 'spirits' },
    { key: 'premix', label: 'PRE-MIX', icon: 'spirits' },
    { key: 'archived', label: 'ARCHIVED', icon: 'originals', archived: true }
  ];

  // Beer / Wine / Spirits are hidden from the top nav. Ticking LEGACY brings them
  // back, which keeps them reachable rather than orphaning their data.
  // Keyed off legacyView (not useNewLayout) so they don't vanish from under you
  // once you've navigated onto one of them.
  const visibleMenuCategories = legacyView
    ? menuCategories
    : menuCategories.filter((category) => !['beer', 'wine', 'spirits'].includes(category.key));

  useEffect(() => {
    const id = editingCocktail?._id || null;
    if (prevEditingCocktailIdRef.current !== id) {
      prevEditingCocktailIdRef.current = id;
      activeCocktailIdRef.current = id;
      setHasUnsavedChanges(false);
      recipeBuilderInteractedRef.current = false;
      // Preserve recipe view when navigating via arrows (preserveRecipeViewRef set by handleNext/handlePrev)
      setRecipeViewActive(preserveRecipeViewRef.current);
      preserveRecipeViewRef.current = false;
    }
  }, [editingCocktail?._id]);

  useEffect(() => {
    activeCocktailIdRef.current = editingCocktail?._id || null;
  }, [editingCocktail?._id]);

  useEffect(() => {
    recipeRef.current = recipe;
  }, [recipe]);

  const recipeDidChange = useCallback((prev, next) => {
    try {
      if (prev === next) return false;
      if (!prev && !next) return false;
      if (!prev || !next) return true;
      const stable = (value) => {
        const seen = new WeakSet();
        const normalize = (v) => {
          if (v === null || v === undefined) return v;
          if (typeof v !== 'object') return v;
          if (v instanceof Date) return v.toISOString();
          if (Array.isArray(v)) return v.map(normalize);
          if (seen.has(v)) return null;
          seen.add(v);
          const out = {};
          Object.keys(v)
            .sort()
            .forEach((k) => {
              out[k] = normalize(v[k]);
            });
          return out;
        };
        return JSON.stringify(normalize(value));
      };
      return stable(prev) !== stable(next);
    } catch (e) {
      return true;
    }
  }, []);

  // Every write originating inside RecipeBuilder is stamped with the cocktail it was rendered
  // for. RecipeBuilder is keyed by item, so navigating unmounts it — but its in-flight async
  // work (ingredient hydration) can still resolve afterwards and call this closure. The
  // identity check below is what stops that result landing on a different item, and mirrors
  // the `prev._id !== myId` guard that already protects Stage 2/3 of navigateToCocktail.
  const handleRecipeBuilderChange = useCallback((ownerId, updatedRecipe) => {
    if (ownerId && ownerId !== activeCocktailIdRef.current) return false;
    if (ownerId && recipeForCocktailIdRef.current && recipeForCocktailIdRef.current !== ownerId) return false;

    const prev = recipeRef.current;
    const isHydrationUpdate = !hasUnsavedChanges &&
      (Date.now() - lastRecipeHydrateAtRef.current) < 1000 &&
      prev &&
      updatedRecipe &&
      updatedRecipe.itemNumber === prev.itemNumber &&
      updatedRecipe.title === prev.title;

    // onlyWhenPresent: if navigation already cleared the recipe, don't let a late
    // low-priority hydration update resurrect it.
    applyRecipe(updatedRecipe, ownerId || recipeForCocktailIdRef.current, { onlyWhenPresent: true });

    if (recipeBuilderInteractedRef.current && !isHydrationUpdate && recipeDidChange(prev, updatedRecipe)) {
      setHasUnsavedChanges(true);
    }
    // Update cocktail name to match recipe title ONLY when user is actively editing.
    // On initial recipe load recipeBuilderInteractedRef is false, preventing the
    // loaded recipe title from overwriting the authoritative cocktail name.
    if (updatedRecipe?.title !== undefined && recipeBuilderInteractedRef.current) {
      setEditingCocktail(prevCocktail => {
        if (!prevCocktail) return prevCocktail;
        if (prevCocktail._id && recipeForCocktailIdRef.current !== prevCocktail._id) return prevCocktail;
        return { ...prevCocktail, name: updatedRecipe.title || prevCocktail.name };
      });
    }
    return true;
  }, [applyRecipe, recipeDidChange, hasUnsavedChanges]);


  // Fetch all cocktails - use ref to prevent race conditions with rapid refreshes
  const fetchCocktails = useCallback(async () => {
    // Prevent multiple simultaneous fetches (race condition protection)
    if (fetchInProgressRef.current) {
      return;
    }
    
    try {
      fetchInProgressRef.current = true;
      setLoading(true);
      // Use ref instead of closure over editingCocktail?._id to avoid stale state
      // and to prevent fetchCocktails from being recreated on every navigation
      const currentEditingId = activeCocktailIdRef.current;
      
      // Use menu-manager endpoint which merges Inventory (source of truth) with Cocktail data
      const data = await apiCall('/menu-items/menu-manager?includeArchived=true');
      const normalized = Array.isArray(data)
        ? data.map((cocktail) => ({
            ...cocktail,
            category: normalizeCategoryKey(cocktail.category || cocktail.section || 'cocktails')
          }))
        : [];

      normalized.sort((a, b) => {
        if (normalizeCategoryKey(a.category) === normalizeCategoryKey(b.category)) {
          return (a.order || 0) - (b.order || 0);
        }
        return normalizeCategoryKey(a.category).localeCompare(normalizeCategoryKey(b.category));
      });

      setCocktails(normalized);
      
      // Guard: if the user navigated during the async fetch, don't override their current position
      if (activeCocktailIdRef.current !== currentEditingId) {
        return normalized;
      }
      
      // If we had an editingCocktail, try to preserve it by finding its index
      if (currentEditingId && !String(currentEditingId).startsWith('new-')) {
        const filtered = normalized.filter(cocktail => {
          const key = normalizeCategoryKey(cocktail.category);
          if (selectedCategory === 'archived') {
            return cocktail.status === 'archived';
          }
          const isActive = cocktail.status !== 'archived' && (cocktail.isActive !== false);
          const categoryMatches = key === normalizeCategoryKey(selectedCategory);
          return isActive && categoryMatches;
        });
        const preservedIndex = filtered.findIndex(c => c._id === currentEditingId);
        if (preservedIndex >= 0) {
          setCurrentIndex(preservedIndex);
        } else if (!currentEditingId) {
          // Only reset to 0 if nothing is being actively edited
          setCurrentIndex(0);
        }
        // If preservedIndex < 0 but currentEditingId is set, the cocktail may be in a
        // different category or the filter is temporarily inconsistent — leave index alone.
      } else {
        setCurrentIndex(0);
      }
      
      return normalized;
    } catch (error) {
      console.error('Error fetching cocktails:', error);
      setCocktails([]);
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, selectedCategory]);

  // Filter cocktails by selected category / status
  // Memoized so the reference only changes when cocktails data or category actually changes,
  // preventing the fallback useEffect from firing on every unrelated re-render (recipe loads, etc.).
  const filteredCocktails = useMemo(() => cocktails.filter(cocktail => {
    const key = normalizeCategoryKey(cocktail.category);
    if (selectedCategory === 'archived') {
      return cocktail.status === 'archived';
    }
    const isActive = cocktail.status !== 'archived' && (cocktail.isActive !== false);
    const categoryMatches = key === normalizeCategoryKey(selectedCategory);
    return isActive && categoryMatches;
  }), [cocktails, selectedCategory]);

  const archivedGroups = useMemo(() => {
    if (selectedCategory !== 'archived') return {};
    return filteredCocktails.reduce((acc, cocktail) => {
      const key = normalizeCategoryKey(cocktail.category) || 'uncategorized';
      if (!acc[key]) acc[key] = [];
      acc[key].push(cocktail);
      return acc;
    }, {});
  }, [filteredCocktails, selectedCategory]);
  
  // Get the current cocktail from the filtered list
  // When editing, use the editing cocktail data; otherwise use the current cocktail from the list
  const editingCocktailId = editingCocktail?._id || null;
  const isNewDraft = Boolean(editingCocktailId && String(editingCocktailId).startsWith('new-'));
  const currentCocktail = editingCocktail || (filteredCocktails[currentIndex] || filteredCocktails[0]) || {};
  const formValues = editingCocktail || {};
  const canArchive = Boolean(editingCocktail && !isNewDraft && editingCocktail.status !== 'archived');
  const canRestore = Boolean(editingCocktail && editingCocktail.status === 'archived');
  const isItemArchived = editingCocktail?.status === 'archived';

  // The recipe in state must be proven to belong to the item on screen before it can be
  // rendered. This makes a mismatched recipe unrenderable rather than merely unlikely — the
  // guards elsewhere stop bad writes, this stops a bad read from ever reaching the user.
  const recipeMatchesCurrentItem = Boolean(editingCocktailId) && recipeOwnerId === editingCocktailId;
  const recipePending = recipeLoading || (Boolean(editingCocktailId) && !recipeMatchesCurrentItem);
  const recipeReady = !recipePending && recipeMatchesCurrentItem && Boolean(recipe);

  // Memoize selected regions to prevent unnecessary re-renders
  const selectedRegions = useMemo(() => {
    if (editingCocktail && Array.isArray(editingCocktail.regions)) return editingCocktail.regions;
    if (currentCocktail && Array.isArray(currentCocktail.regions)) return currentCocktail.regions;
    return [];
  }, [editingCocktail?.regions, currentCocktail?.regions]);

  const getSelectedRegions = useCallback(() => {
    return selectedRegions;
  }, [selectedRegions]);

  const setSelectedRegions = useCallback((next) => {
    setEditingCocktail(prev => (prev ? { ...prev, regions: next } : prev));
    setHasUnsavedChanges(true);
  }, []);

  const toggleBaseSpirit = useCallback((value) => {
    setEditingCocktail(prev => {
      if (!prev) return prev;
      const current = Array.isArray(prev.baseSpirits) ? prev.baseSpirits : [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, baseSpirits: next };
    });
    setHasUnsavedChanges(true);
  }, []);

  const toggleRegion = useCallback((code) => {
    if (!code) return;
    const upper = String(code).toUpperCase();
    const current = selectedRegions;
    const exists = current.includes(upper);
    const next = exists ? current.filter(c => c !== upper) : [...current, upper];
    setSelectedRegions(next);
  }, [selectedRegions]);

  // Ref kept current so map click handlers always call the latest toggleRegion,
  // even though the SVG is never re-mounted and the closure from setup time is stale.
  const toggleRegionRef = useRef(toggleRegion);
  useEffect(() => {
    toggleRegionRef.current = toggleRegion;
  }, [toggleRegion]);

  const handleCountrySelectChange = useCallback((event) => {
    const options = Array.from(event.target.selectedOptions || []);
    const next = options
      .map(option => String(option.value || '').toUpperCase())
      .filter(Boolean);
    setSelectedRegions(next);
  }, [setSelectedRegions]);

  useEffect(() => {
    // Skip if we're in the middle of a programmatic navigation (row click or arrow navigation)
    // The navigation handler already set editingCocktail directly
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }
    
    // Don't overwrite new drafts - let user continue editing
    if (editingCocktailId && String(editingCocktailId).startsWith('new-')) {
      // Update category if it changed, but keep the draft
      setEditingCocktail(prev => {
        if (prev && prev.category !== selectedCategory && selectedCategory !== 'archived') {
          return { ...prev, category: selectedCategory };
        }
        return prev;
      });
      return;
    }
    // ID-first lookup: prevents index drift when a server refresh reorders the list.
    // Without this, filteredCocktails[currentIndex] could point to the wrong cocktail
    // (e.g., Birchwood at index 0) after fetchCocktails returns fresh data.
    const activeId = activeCocktailIdRef.current;
    const fallbackById = activeId ? filteredCocktails.find(c => c._id === activeId) : null;
    const fallback = fallbackById || filteredCocktails[currentIndex] || filteredCocktails[0];
    if (!fallback) {
      // Only clear editingCocktail if we're not in the middle of creating a new item
      // This allows "New Item" to work even when category is empty
      if (!editingCocktailId || !String(editingCocktailId).startsWith('new-')) {
        setEditingCocktail(null);
      }
      return;
    }
    // Only update if the active cocktail ref doesn't already match the fallback.
    // Using activeCocktailIdRef (a ref, not state) prevents this effect from running
    // on every field edit (which editingCocktail in deps would cause).
    const shouldUpdate = (!activeId || activeId !== fallback._id);
    
    if (shouldUpdate) {
      // Don't overwrite if we're creating a new item
      if (!editingCocktailId || !String(editingCocktailId).startsWith('new-')) {
        // Only update if we don't already have this cocktail set
        // This prevents the "collapse" issue where correct highlights get overwritten
        setEditingCocktail(prev => {
          // If prev exists and matches fallback, don't update (prevents unnecessary re-renders)
          if (prev && prev._id === fallback._id) {
            return prev;
          }
          return { ...fallback };
        });
        // Sync mapType from loaded cocktail data (use saved value or default to 'world')
        setMapType(inferMapType(fallback));
        setVideoUpload(null);
        setVideoPreviewUrl(prev => {
          if (prev) {
            URL.revokeObjectURL(prev);
          }
          return '';
        });
      }
    }
  }, [filteredCocktails, currentIndex, editingCocktailId, selectedCategory]);

  // Fetch countries list for selection
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await apiCall('/countries');
        if (!cancelled) {
          if (Array.isArray(list) && list.length > 0) {
            setCountries(buildCountryList(list));
          } else {
            // Fallback: parse from client SVG if server returns empty
            try {
              const svgUrl = `${process.env.PUBLIC_URL || ''}/assets/images/worldmap.svg`;
              const res = await fetch(svgUrl);
              const svgText = await res.text();
              const re = /<path[^>]*\sid="([A-Za-z0-9_-]+)"[^>]*\sname="([^"]+)"[^>]*>/g;
              const arr = [];
              let m;
              while ((m = re.exec(svgText)) !== null) {
                arr.push({ code: String(m[1]).toUpperCase(), name: m[2], svgId: String(m[1]).toUpperCase() });
              }
              setCountries(buildCountryList(arr));
            } catch (e2) {
              console.warn('Countries SVG fallback failed', e2);
              setCountries(buildCountryList());
            }
          }
        }
      } catch (e) {
        console.warn('Failed to load countries', e);
        if (!cancelled) {
          // Fallback: parse from client SVG when API fails
          try {
            const svgUrl = `${process.env.PUBLIC_URL || ''}/assets/images/worldmap.svg`;
            const res = await fetch(svgUrl);
            const svgText = await res.text();
            const re = /<path[^>]*\sid="([A-Za-z0-9_-]+)"[^>]*\sname="([^"]+)"[^>]*>/g;
            const arr = [];
            let m;
            while ((m = re.exec(svgText)) !== null) {
              arr.push({ code: String(m[1]).toUpperCase(), name: m[2], svgId: String(m[1]).toUpperCase() });
            }
            setCountries(buildCountryList(arr));
          } catch (e2) {
            console.warn('Countries SVG fallback failed', e2);
            setCountries(buildCountryList());
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall]);

  const slugify = (value) => {
    if (!value) return 'map';
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'map';
  };

  // Save map as PNG directly to server (mandatory for all non-premix categories)
  const saveMapSnapshot = async (itemNumber) => {
    if (!itemNumber || !Number.isFinite(itemNumber)) {
      console.warn('⚠️ Cannot save map snapshot: no itemNumber provided');
      return;
    }

    // Use direct SVG ref - the actual SVG element (no searching)
    let svg = svgRef.current;
    let attempts = 0;
    const maxAttempts = 50; // Increased from 20 to 50 (5 seconds max)
    
    // Wait for SVG to be ready
    while (!svg && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      svg = svgRef.current;
      attempts++;
    }
    
    if (!svg) {
      console.error(`[MenuManager] Map SVG not ready after ${attempts} attempts (${attempts * 100}ms). svgRef.current:`, svgRef.current);
      throw new Error('Map SVG not ready. Please ensure the map is fully loaded.');
    }

    // Ensure highlights are up to date before saving
    // selectedRegions comes from editingCocktail.regions - this is the current state
    refreshMapHighlights();
    
    // Wait a moment for DOM updates to apply
    await new Promise(resolve => setTimeout(resolve, 50));

    // Clone the SVG with all current styles
    const clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    
    // Remove white background rectangles
    const rects = clone.querySelectorAll('rect');
    rects.forEach(rect => {
      const fill = rect.getAttribute('fill') || rect.style.fill || '';
      const fillLower = fill.toLowerCase();
      if (fillLower === '#ffffff' || fillLower === '#fff' || fillLower === 'white' || 
          fillLower === '#f5f5f5' || fillLower === '#fafafa' || fillLower === 'rgb(255, 255, 255)') {
        rect.remove();
      }
    });
    
    clone.style.backgroundColor = 'transparent';
    clone.style.background = 'transparent';
    
    // Use appropriate viewBox based on map type
    if (!clone.getAttribute('viewBox')) {
      const defaultViewBox = mapType === 'us' ? '0 0 959 593' : '0 0 2000 857';
      clone.setAttribute('viewBox', defaultViewBox);
    }
    clone.setAttribute('width', '1200');
    clone.setAttribute('height', '600');
    
    // Serialize SVG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clone);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = (event) => {
          // Event objects don't have .message, so wrap in Error
          reject(new Error(`Failed to load SVG image: ${event.type || 'unknown error'}`));
        };
        img.src = svgUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 600;
      const ctx = canvas.getContext('2d', { willReadFrequently: true, alpha: true });
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) resolve(result);
          else reject(new Error('Failed to render map snapshot'));
        }, 'image/png', 1.0);
      });

      // Send PNG directly to server
      const formData = new FormData();
      formData.append('map', blob, `${itemNumber}.png`);
      
      console.log(`[MenuManager] Uploading map snapshot for item ${itemNumber}, blob size: ${blob.size} bytes`);
      const response = await apiCall(`/menu-items/map/${itemNumber}`, {
        method: 'POST',
        body: formData
      });
      
      console.log(`[MenuManager] Map upload response:`, response);
      if (!response || !response.success) {
        throw new Error(response?.message || 'Server did not confirm map save');
      }
      
    } catch (error) {
      console.error('Error saving map snapshot:', error);
      throw error;
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  };

  // Helper function to show notification modal instead of browser alert
  const showNotification = (message, type = 'info', title = '') => {
    const defaultTitles = {
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Notice'
    };
    setNotificationModal({
      show: true,
      title: title || defaultTitles[type] || 'Notice',
      message,
      type
    });
  };

  const buildCocktailFormData = async (data, options = {}) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('concept', data.concept || '');
    formData.append('ingredients', data.ingredients || '');
    formData.append('globalIngredients', data.globalIngredients || '');
    // Garnish is NOT saved to cocktail model - it comes from RecipeBuilder (recipe.metadata.garnish)
    formData.append('category', normalizeCategoryKey(data.category || selectedCategory));
    formData.append('regions', JSON.stringify(data.regions || selectedRegions || []));
    // Sent unconditionally so clearing every button persists, rather than the server
    // keeping the previous selection because the field was absent.
    formData.append('baseSpirits', JSON.stringify(Array.isArray(data.baseSpirits) ? data.baseSpirits : []));
    formData.append('order', typeof data.order === 'number' ? data.order : 0);
    if (typeof data.featured !== 'undefined') {
      formData.append('featured', data.featured ? 'true' : 'false');
    }
    // Append itemId and itemNumber for video filename
    if (data.itemId) {
      formData.append('itemId', data.itemId);
    }
    if (data.itemNumber && Number.isFinite(data.itemNumber)) {
      formData.append('itemNumber', String(data.itemNumber));
    }
    // Append mapType for world/us map selection
    if (data.mapType) {
      formData.append('mapType', data.mapType);
    }
    // Append display flag (false = hidden on website, true = visible).
    // Archived items are always hidden, whatever the item carried before it was archived.
    const archivedItem = data.status === 'archived';
    formData.append('display', (archivedItem || data.display === false) ? 'false' : 'true');

    if (options.videoFile instanceof File) {
      formData.append('video', options.videoFile);
    } else {
    }

    return formData;
  };

  const handleVideoSelection = (file) => {
    if (!file) return;
    setHasUnsavedChanges(true);
    
    // Get itemNumber for processing
    const itemNumber = editingCocktail?.itemNumber || currentCocktail?.itemNumber;
    if (!itemNumber) {
      showNotification('Please save the item first to get an item number.', 'warning');
      return;
    }
    
    // Show video options modal
    setVideoOptionsModal({
      show: true,
      file: file,
      itemNumber: itemNumber
    });
  };
  
  const handleVideoOption = async (option) => {
    const { file, itemNumber } = videoOptionsModal;
    
    console.log('[MenuManager] handleVideoOption called', { option, file: file?.name, itemNumber });
    
    // Close modal
    setVideoOptionsModal({ show: false, file: null, itemNumber: null });
    
    if (!file || !itemNumber) {
      console.warn('[MenuManager] handleVideoOption: Missing file or itemNumber', { file: !!file, itemNumber });
      return;
    }
    
    switch (option) {
      case 'process':
        if (isRenderSite) {
          showNotification('Video processing on local site only', 'warning');
          return;
        }
        if (!workerStatus?.online) {
          showNotification('Local worker is offline. Start the worker + tunnel first, then try again.', 'warning');
          return;
        }
        await handleProcessVideo(file, itemNumber);
        break;
      case 'upload':
        handleUploadVideo(file);
        break;
      case 'upload-icon':
        await handleUploadIcon(file, itemNumber);
        break;
      case 'cancel':
        // Do nothing, just close
        break;
      default:
        break;
    }
  };
  
  const handleProcessVideo = async (file, itemNumber) => {
    console.log('[MenuManager] handleProcessVideo called', { file: file?.name, itemNumber, API_BASE_URL });
    
    try {
      // Set preview
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
      const previewUrl = URL.createObjectURL(file);
      setVideoPreviewUrl(previewUrl);
      setVideoUpload(file);
      if (videoRef.current) {
        videoRef.current.src = previewUrl;
      }
      
      // Initialize processing status
      console.log('[MenuManager] Setting processing status to active for item', itemNumber);
      setProcessingStatus({
        active: true,
        stage: 'creating-job',
        progress: 0,
        total: 100,
        message: 'Creating job...',
        error: null,
        itemNumber: itemNumber // Include itemNumber so overlay can match
      });

      // Step 1: Create job on Render backend (returns worker upload URL + upload token)
      const jobStart = await apiCall(`/video-jobs/${itemNumber}/start`, {
        method: 'POST',
      });

      const workerUploadUrl = jobStart?.workerUploadUrl;
      const uploadToken = jobStart?.uploadToken;
      if (!workerUploadUrl || !uploadToken) {
        throw new Error('Job start failed: missing workerUploadUrl or uploadToken');
      }

      setProcessingStatus((prev) => ({
        ...(prev || {}),
        active: true,
        stage: 'uploading-to-worker',
        progress: 1,
        total: 100,
        message: 'Uploading to local worker...',
        error: null,
        itemNumber,
      }));

      // Step 2: Upload raw video directly to the local worker (via Cloudflare Tunnel URL)
      const formData = new FormData();
      formData.append('video', file);

      console.log('[MenuManager] Starting upload to worker:', { 
        workerUploadUrl, 
        fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        fileName: file.name 
      });

      // Add timeout for large file uploads (5 minutes = 300 seconds for large files)
      const uploadTimeoutMs = 5 * 60 * 1000; // 5 minutes
      const uploadToWorkerWithProgress = (url, token, body, onProgress) =>
        new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url);
          xhr.setRequestHeader('X-Upload-Token', token);
          xhr.timeout = uploadTimeoutMs;

          xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            onProgress?.(event.loaded, event.total);
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(xhr.responseText ? JSON.parse(xhr.responseText) : {});
              } catch (e) {
                resolve({});
              }
              return;
            }
            reject(
              new Error(
                `Worker upload failed: ${xhr.status} ${xhr.statusText} ${xhr.responseText || ''}`.trim()
              )
            );
          };

          xhr.onerror = () => reject(new Error('Worker upload failed: network error'));
          xhr.ontimeout = () =>
            reject(
              new Error(
                `Upload timeout after ${uploadTimeoutMs / 1000} seconds. The file may be too large or the worker may be unreachable.`
              )
            );

          xhr.send(body);
        });

      let lastUploadPct = -1;
      const workerResult = await uploadToWorkerWithProgress(
        workerUploadUrl,
        uploadToken,
        formData,
        (loaded, total) => {
          const pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
          if (pct === lastUploadPct) return;
          lastUploadPct = pct;
          setProcessingStatus((prev) => ({
            ...(prev || {}),
            active: true,
            stage: 'uploading-to-worker',
            progress: pct,
            total: 100,
            message: `Uploading to local worker... ${pct}%`,
            error: null,
            itemNumber,
          }));
        }
      );
      console.log('[MenuManager] Upload to worker successful:', workerResult);

      // Set status to "uploaded, waiting for worker to process"
      setProcessingStatus(prev => ({
        ...(prev || {}),
        active: true,
        stage: 'uploaded',
        progress: 100,
        total: 100,
        message: 'Uploaded. Waiting for worker to start processing...',
        error: null,
        itemNumber,
      }));

      // Begin polling job status from Render backend (DB-backed)
      startProcessingPoll(itemNumber);
      
    } catch (error) {
      console.error('[MenuManager] Process video error:', error);
      setProcessingStatus(prev => ({
        ...prev,
        active: false,
        error: error.message,
        message: `Error: ${error.message}`
      }));
    }
  };
  
  const handleUploadVideo = (file) => {
    // Current upload behavior (as-is)
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    setVideoPreviewUrl(previewUrl);
    setVideoUpload(file);
    if (videoRef.current) {
      videoRef.current.src = previewUrl;
    }
    setEditingCocktail(prev => {
      if (prev) {
        return { ...prev, videoFile: file.name };
      }
      if (currentCocktail && currentCocktail._id) {
        return { ...currentCocktail, videoFile: file.name };
      }
      return {
        name: '',
        concept: '',
        ingredients: '',
        globalIngredients: '',
        garnish: '',
        category: selectedCategory,
        videoFile: file.name,
        regions: selectedRegions
      };
    });
  };
  
  const handleUploadIcon = async (file, itemNumber) => {
    try {
      // Set preview
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
      const previewUrl = URL.createObjectURL(file);
      setVideoPreviewUrl(previewUrl);
      
      // Initialize processing status
      setProcessingStatus({
        active: true,
        stage: 'creating-icon',
        progress: 0,
        total: 100,
        message: 'Creating icon version...',
        error: null,
        itemNumber: itemNumber // Include itemNumber so overlay can match
      });
      
      // Upload and process icon
      const formData = new FormData();
      formData.append('video', file);
      
      const response = await fetch(`${API_BASE_URL}/api/video-processing/process-icon/${itemNumber}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to start icon processing');
      }
      
      // Start polling for status
      startProcessingPoll(itemNumber);
      
    } catch (error) {
      console.error('Upload icon error:', error);
      setProcessingStatus(prev => ({
        ...prev,
        active: false,
        error: error.message,
        message: `Error: ${error.message}`
      }));
    }
  };
  
  const startProcessingPoll = (itemNumber) => {
    console.log('[MenuManager] startProcessingPoll called for item', itemNumber);
    
    // Clear any existing interval
    if (processingPollIntervalRef.current) {
      clearInterval(processingPollIntervalRef.current);
    }
    
    // Poll every 2 seconds
    processingPollIntervalRef.current = setInterval(async () => {
      try {
        const status = await apiCall(`/video-jobs/${itemNumber}/status`);
        console.log('[MenuManager] Poll response for item', itemNumber, ':', status);
        if (status) {
          // If a job is active but worker is offline, surface it explicitly.
          const hydrated =
            status?.active && status?.workerOnline === false
              ? {
                  ...status,
                  itemNumber: itemNumber, // Ensure itemNumber is always set
                  stage: 'worker-disconnected',
                  message: 'Local worker disconnected',
                }
              : {
                  ...status,
                  itemNumber: itemNumber, // Ensure itemNumber is always set
                };
          console.log('[MenuManager] Setting processingStatus:', hydrated);
          setProcessingStatus(hydrated);
          
          // Stop polling if not active
          if (!hydrated.active) {
            console.log('[MenuManager] Processing no longer active, stopping poll');
            clearInterval(processingPollIntervalRef.current);
            processingPollIntervalRef.current = null;
            
            // Refresh cocktails list to get updated video
            if (hydrated.stage === 'complete' && !hydrated.error) {
              console.log('[MenuManager] Processing complete! Refreshing cocktails list...');
              fetchCocktails().then((cocktails) => {
                console.log('[MenuManager] Cocktails refreshed:', cocktails?.length, 'items');
                // Find the item that was just processed
                const processedItem = cocktails?.find(c => c.itemNumber === itemNumber);
                if (processedItem) {
                  console.log('[MenuManager] Processed item data:', {
                    itemNumber: processedItem.itemNumber,
                    name: processedItem.name,
                    cloudinaryVideoUrl: processedItem.cloudinaryVideoUrl,
                    cloudinaryIconUrl: processedItem.cloudinaryIconUrl,
                    videoUrl: processedItem.videoUrl
                  });
                  
                  // Update editingCocktail if it matches the processed item
                  setEditingCocktail(prev => {
                    if (prev && (prev.itemNumber === itemNumber || prev._id === processedItem._id)) {
                      console.log('[MenuManager] Updating editingCocktail with new Cloudinary URL:', processedItem.cloudinaryVideoUrl);
                      return {
                        ...prev,
                        cloudinaryVideoUrl: processedItem.cloudinaryVideoUrl,
                        cloudinaryIconUrl: processedItem.cloudinaryIconUrl,
                        cloudinaryVideoPublicId: processedItem.cloudinaryVideoPublicId,
                        cloudinaryIconPublicId: processedItem.cloudinaryIconPublicId,
                        videoUrl: processedItem.cloudinaryVideoUrl || prev.videoUrl // Also update videoUrl to Cloudinary URL
                      };
                    }
                    return prev;
                  });
                  
                  // Also update the cocktail in the cocktails state array if it matches
                  // This ensures currentCocktail (when not editing) gets the updated URL
                  setCocktails(prevCocktails => {
                    const updated = prevCocktails.map(c => {
                      if (c.itemNumber === itemNumber || c._id === processedItem._id) {
                        console.log('[MenuManager] Updating cocktail in state array with Cloudinary URL:', processedItem.cloudinaryVideoUrl);
                        return {
                          ...c,
                          cloudinaryVideoUrl: processedItem.cloudinaryVideoUrl,
                          cloudinaryIconUrl: processedItem.cloudinaryIconUrl,
                          cloudinaryVideoPublicId: processedItem.cloudinaryVideoPublicId,
                          cloudinaryIconPublicId: processedItem.cloudinaryIconPublicId,
                          videoUrl: processedItem.cloudinaryVideoUrl || c.videoUrl // Also update videoUrl to Cloudinary URL
                        };
                      }
                      return c;
                    });
                    return updated;
                  });
                  
                  // Also clear videoPreviewUrl since we now have the Cloudinary URL
                  if (videoPreviewUrl) {
                    URL.revokeObjectURL(videoPreviewUrl);
                    setVideoPreviewUrl('');
                  }
                } else {
                  console.warn('[MenuManager] Processed item not found in refreshed list:', itemNumber);
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('Status poll error:', error);
      }
    }, 2000);
  };
  
  // Stop polling when component unmounts or item changes
  useEffect(() => {
    return () => {
      if (processingPollIntervalRef.current) {
        clearInterval(processingPollIntervalRef.current);
      }
    };
  }, []);
  
  // Check if current item is processing and restore status if needed
  useEffect(() => {
    const currentItemNumber = editingCocktail?.itemNumber || currentCocktail?.itemNumber;
    
    if (!currentItemNumber) {
      // No item selected - stop polling but keep status (user might navigate back)
      if (processingPollIntervalRef.current) {
        clearInterval(processingPollIntervalRef.current);
        processingPollIntervalRef.current = null;
      }
      return;
    }
    
    // Check if we have status for this item
    const hasStatusForItem = processingStatus && processingStatus.itemNumber === currentItemNumber;
    
    // If we have status for a different item, clear it first
    if (processingStatus && processingStatus.itemNumber !== currentItemNumber) {
      // Stop polling for the old item
      if (processingPollIntervalRef.current) {
        clearInterval(processingPollIntervalRef.current);
        processingPollIntervalRef.current = null;
      }
      // Clear the old status
      setProcessingStatus(null);
    }
    
    // If we don't have status for this item, check server
    if (!hasStatusForItem) {
      // Check if this item is processing on the server
      const checkStatus = async () => {
        try {
          const status = await apiCall(`/video-jobs/${currentItemNumber}/status`);
          
          if (status && status.active) {
              // Guard: don't overwrite a locally-initiated upload that started
              // while this async call was in flight (race condition).
              setProcessingStatus((prev) => {
                if (prev && prev.itemNumber === currentItemNumber &&
                    (prev.stage === 'creating-job' || prev.stage === 'uploading-to-worker')) {
                  console.log('[MenuManager] Skipping server restore — local upload already in progress for item', currentItemNumber);
                  return prev;
                }
                console.log('[MenuManager] Restoring processing status for item', currentItemNumber, status);
                return {
                  ...status,
                  itemNumber: currentItemNumber,
                };
              });
              if (!processingPollIntervalRef.current) {
                startProcessingPoll(currentItemNumber);
              }
          }
        } catch (error) {
          console.error('Error checking processing status:', error);
        }
      };
      
      checkStatus();
    } else if (processingStatus.active) {
      // We have status for this item and it's active - ensure polling is running.
      // Avoid starting polling while the browser is still uploading to the worker,
      // otherwise polling will overwrite the upload progress UI with backend status.
      const shouldPoll =
        processingStatus.stage !== 'creating-job' &&
        processingStatus.stage !== 'uploading-to-worker';
      if (shouldPoll && !processingPollIntervalRef.current) {
        startProcessingPoll(currentItemNumber);
      }
    } else {
      // Status exists but not active - stop polling
      if (processingPollIntervalRef.current) {
        clearInterval(processingPollIntervalRef.current);
        processingPollIntervalRef.current = null;
      }
    }
  }, [editingCocktail?.itemNumber, currentCocktail?.itemNumber]);
  
  // Also check processingStatus changes to ensure polling is active
  useEffect(() => {
    const currentItemNumber = editingCocktail?.itemNumber || currentCocktail?.itemNumber;
    if (processingStatus && processingStatus.active && processingStatus.itemNumber === currentItemNumber) {
      const shouldPoll =
        processingStatus.stage !== 'creating-job' &&
        processingStatus.stage !== 'uploading-to-worker';
      if (shouldPoll && !processingPollIntervalRef.current) {
        startProcessingPoll(currentItemNumber);
      }
    } else if (processingStatus && (!processingStatus.active || processingStatus.itemNumber !== currentItemNumber)) {
      // Stop polling if status is not active or for different item
      if (processingPollIntervalRef.current) {
        clearInterval(processingPollIntervalRef.current);
        processingPollIntervalRef.current = null;
      }
    }
  }, [processingStatus]);

  const renderTableRow = (cocktail, rowIndex) => {
    const resolvedIndex = typeof rowIndex === 'number'
      ? rowIndex
      : filteredCocktails.findIndex(item => item._id === cocktail._id);
    const isActiveRow = (editingCocktailId && editingCocktailId === cocktail._id) || resolvedIndex === currentIndex;

    return (
      <tr
        key={`${cocktail._id}-${resolvedIndex}`}
        onClick={() => {
          if (resolvedIndex >= 0) {
            const clickedCocktail = filteredCocktails[resolvedIndex];
            if (clickedCocktail) {
              navigateToCocktail(clickedCocktail, resolvedIndex);
            }
          }
        }}
        className={`border-b border-gray-100 hover:bg-gray-50 ${
          isActiveRow ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
        }`}
        style={{ cursor: 'pointer' }}
      >
        <td className="py-3 px-4">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {menuCategories.find(c => normalizeCategoryKey(c.key) === normalizeCategoryKey(cocktail.category))?.label || cocktail.category}
          </span>
        </td>
        <td className="py-3 px-4">
          <div>
            <div className="font-medium text-gray-900">{cocktail.name}</div>
            <div className="text-sm text-gray-500">
              {cocktail.concept ? cocktail.concept.substring(0, 60) + '...' : 'No concept'}
            </div>
          </div>
        </td>
        <td className="py-3 px-4 text-sm text-gray-600">
          {cocktail.itemNumber || '—'}
        </td>
        <td className="py-3 px-4">
          <div className="flex gap-2">
            <button
              onClick={() => handleDelete(cocktail._id)}
              className="btn btn-sm btn-danger"
            >
              Delete
            </button>
            {cocktail.status === 'archived' ? (
              <button
                onClick={() => handleRestore(cocktail)}
                className="btn btn-sm"
                style={{ backgroundColor: '#10b981', color: '#fff' }}
              >
                Restore
              </button>
            ) : (
              <button
                onClick={() => handleArchive(cocktail)}
                className="btn btn-sm"
                style={{ backgroundColor: '#fbbf24', color: '#111' }}
              >
                Archive
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  // US States list for the US map
  const usStates = useMemo(() => buildStateList(), []);
  const stateLookup = useMemo(() => buildStateMap(), []);

  // Filtered list based on mapType
  const filteredCountries = countries.filter(c => {
    if (!countryQuery) return true;
    const q = countryQuery.toLowerCase();
    return (c.name && c.name.toLowerCase().includes(q)) || (c.code && c.code.toLowerCase().includes(q));
  });

  const filteredStates = usStates.filter(s => {
    if (!countryQuery) return true;
    const q = countryQuery.toLowerCase();
    return (s.name && s.name.toLowerCase().includes(q)) || (s.code && s.code.toLowerCase().includes(q));
  });

  // Use the appropriate list based on mapType
  const filteredRegionsList = mapType === 'us' ? filteredStates : filteredCountries;

  // ARCHIVED is a status, not a section: it holds items from every category. The editor
  // there follows the item's own category, so an archived Social Tonic gets the Social
  // Tonics editor (new view plus the LEGACY toggle) and an archived pre-mix gets the
  // pre-mix layout. Data flow (filtering, saving, new items) still keys off selectedCategory.
  const editorCategory = normalizeCategoryKey(
    normalizeCategoryKey(selectedCategory) === 'archived'
      ? (editingCocktail?.category || currentCocktail?.category || 'cocktails')
      : selectedCategory
  );
  // Social Tonics and CLASSICS hide the legacy fields (map / countries panel, Concept) by
  // default; ticking LEGACY brings them back. Every other category shows them as before.
  const isLegacyToggleCategory = ['cocktails', 'mocktails'].includes(editorCategory);
  const showLegacyFields = !isLegacyToggleCategory || legacyView;
  // True only in the new (non-legacy) view. Gate every layout tweak on this so the
  // legacy view keeps rendering exactly as it always has.
  const useNewLayout = isLegacyToggleCategory && !legacyView;

  // In the new layout the left column's content starts flush with the SAVE CHANGES
  // button. That button sits in a centred, fixed-width flex row, so its left edge
  // depends on the rendered button widths — measure it rather than hardcoding.
  const [contentLeftOffset, setContentLeftOffset] = useState(null);
  // Header padding that lines VIEW RECIPE up with the title's left border and
  // ARCHIVED up with the ARCHIVE button's right border.
  const [headerPadding, setHeaderPadding] = useState(null);
  const measureContentLeftOffset = useCallback(() => {
    const button = saveButtonRef.current;
    const container = viewerContainerRef.current;
    if (!button || !container) return;
    const buttonRect = button.getBoundingClientRect();
    // A not-yet-laid-out node reports an all-zero rect; ignore it so we don't latch
    // onto a bogus offset before the action bar has mounted.
    if (buttonRect.width === 0) return;
    const containerRect = container.getBoundingClientRect();
    const offset = buttonRect.left - containerRect.left;
    setContentLeftOffset(offset > 0 ? offset : null);

    // The header is absolutely positioned against a different box than the viewer,
    // so measure the gap between them rather than assuming the parent's padding.
    // The action bar is centre-aligned, so its right gap mirrors this left offset.
    const header = headerRef.current;
    if (!header || offset <= 0) return;
    const headerRect = header.getBoundingClientRect();
    const left = (containerRect.left - headerRect.left) + offset;
    const right = (headerRect.right - containerRect.right) + offset;
    // Bail out when unchanged — a fresh object every pass would loop the render.
    setHeaderPadding((prev) => (prev && prev.left === left && prev.right === right)
      ? prev
      : { left, right });
  }, []);

  // Callback ref: fires the moment the button mounts, which is what the plain effect
  // missed — the action bar appears only once item data has loaded.
  const attachSaveButton = useCallback((node) => {
    saveButtonRef.current = node;
    if (node) measureContentLeftOffset();
  }, [measureContentLeftOffset]);

  useLayoutEffect(() => {
    if (!useNewLayout) {
      setContentLeftOffset(null);
      setHeaderPadding(null);
      return;
    }
    measureContentLeftOffset();
    const button = saveButtonRef.current;
    if (!button || !window.ResizeObserver) return;
    const observer = new ResizeObserver(() => measureContentLeftOffset());
    observer.observe(button);
    return () => observer.disconnect();
  }, [useNewLayout, measureContentLeftOffset, viewerSize?.width, viewerSize?.height, currentCocktail?._id]);

  // Column keeps its original content width (33.33% minus the old 48px padding) and
  // grows by however much padding the alignment needs, so nothing gets squeezed.
  const leftColumnPadding = useNewLayout && contentLeftOffset != null ? `${contentLeftOffset}px` : '48px';
  const leftColumnWidth = useNewLayout && contentLeftOffset != null
    ? `calc(33.33% - 48px + ${contentLeftOffset}px)`
    : '33.33%';

  const countryLookup = useMemo(() => buildCountryMap(countries), [countries]);
  
  // Combined lookup for both countries and states
  const regionLookup = useMemo(() => {
    return mapType === 'us' ? stateLookup : countryLookup;
  }, [mapType, stateLookup, countryLookup]);

  const selectedCountryDetails = useMemo(() => {
    return selectedRegions
      .map((code) => {
        const upper = String(code || '').toUpperCase();
        if (!upper) return null;
        const entry = regionLookup[upper];
        return {
          code: upper,
          name: entry?.name || upper
        };
      })
      .filter(Boolean);
  }, [selectedRegions, regionLookup]);

  // Load SVG map asset based on mapType - reload when mapType changes
  useEffect(() => {
    let cancelled = false;
    
    const loadSvg = async () => {
      try {
        const svgFile = mapType === 'us' ? 'Blank_US_Map.svg' : 'worldmap.svg';
        const svgUrl = `${process.env.PUBLIC_URL || ''}/assets/images/${svgFile}`;
        // Use default cache to allow browser caching for faster subsequent loads
        const res = await fetch(svgUrl);
        if (!res.ok) {
          throw new Error(`Failed to fetch SVG (${res.status})`);
        }
        const svgText = await res.text();
        if (!svgText || svgText.trim().length === 0) {
          throw new Error('SVG content is empty');
        }
        if (!cancelled) {
          // Reset map state to force re-mount of SVG
          setMapLoaded(false);
          setMapSvgContent(svgText);
          setMapError('');
          // mapLoaded(true) is set inside handleMapReady, after processBatch
          // has finished stamping all [data-code] attributes. Using a fixed
          // timeout here caused highlights to fire before paths were ready.
        }
      } catch (err) {
        console.warn(`Failed to load ${mapType} map SVG`, err);
        if (!cancelled) {
          setMapSvgContent('');
          setMapError('Map failed to load. Please refresh the page.');
          setMapLoaded(false);
        }
      }
    };
    
    loadSvg();
    return () => {
      cancelled = true;
    };
  }, [mapType]);

  // Ref so handleMapReady can always call the latest refreshMapHighlights without a dep cycle
  const refreshMapHighlightsRef = useRef(null);

  // Update highlights via direct DOM manipulation - never re-render SVG
  const refreshMapHighlights = useCallback(() => {
    if (!mapRef.current) return;
    const selected = new Set(selectedRegions);
    const paths = mapRef.current.querySelectorAll('[data-code]');
    paths.forEach(pathEl => {
      const code = pathEl.getAttribute('data-code');
      if (!code) return;
      const isSelected = selected.has(code);
      const fillColor = isSelected ? '#666666' : '#d0d0d0';
      pathEl.style.setProperty('fill', fillColor, 'important');
      pathEl.style.setProperty('stroke', fillColor, 'important');
      pathEl.style.setProperty('stroke-width', '0.4', 'important');
    });
  }, [selectedRegions]);

  // Keep the ref current whenever refreshMapHighlights is recreated
  useEffect(() => {
    refreshMapHighlightsRef.current = refreshMapHighlights;
  }, [refreshMapHighlights]);

  // Set up click handlers once when map is ready
  const handleMapReady = useCallback(() => {
    if (!mapRef.current) return;

    const container = mapRef.current;
    const svg = container.querySelector('svg');
    if (!svg) return;

    // Wait for paths to be ready (processBatch stamps [data-code] asynchronously)
    const setupInteractions = () => {
      const interactivePaths = container.querySelectorAll('[data-code]');
      if (interactivePaths.length === 0) {
        setTimeout(setupInteractions, 50);
        return;
      }

      const handlePathClick = (event) => {
        const code = event.target?.dataset?.code;
        if (code) {
          // Use ref so we always call the latest toggleRegion, not a stale closure
          toggleRegionRef.current(code);
        }
      };

      // Attach click handlers to all interactive paths
      interactivePaths.forEach(pathEl => {
        // Remove any existing listener to avoid duplicates
        pathEl.removeEventListener('click', handlePathClick);
        pathEl.addEventListener('click', handlePathClick);
      });

      // All paths are stamped — apply highlights immediately (covers case where mapLoaded
      // was already true so setMapLoaded(true) below won't trigger the effect).
      if (refreshMapHighlightsRef.current) refreshMapHighlightsRef.current();
      setMapLoaded(true);
    };

    setupInteractions();
  }, []);

  // Update highlights when regions change or when editingCocktail changes (SVG never re-renders)
  useEffect(() => {
    if (mapLoaded && mapRef.current) {
      // Check if paths are ready before highlighting
      const paths = mapRef.current.querySelectorAll('[data-code]');
      if (paths.length > 0) {
        // Minimal delay - paths are already ready
        const timeoutId = setTimeout(() => {
          refreshMapHighlights();
        }, 10);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [refreshMapHighlights, mapLoaded, editingCocktail?.regions, editingCocktail?._id]);

  useEffect(() => {
    fetchCocktails();
  }, [fetchCocktails]);

  // Debug: Log filtered cocktails when they change
  useEffect(() => {
    const classicsCocktails = cocktails.filter(c => {
      const key = normalizeCategoryKey(c.category);
      return key === 'classics';
    });
  }, [filteredCocktails, selectedCategory, cocktails.length, cocktails]);

  // Video canvas drawing effect
  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    let animationFrameId;

    // Initialize canvas with default dimensions if needed
    if (!canvas.width || !canvas.height) {
      canvas.width = 640;
      canvas.height = 360;
    }

    // If there's no video file or preview, clear the canvas
    if (!currentCocktail.videoFile && !videoPreviewUrl) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    // Ensure video is playing
    const playVideo = async () => {
      if (video.paused && video.readyState >= 2) {
        try {
          await video.play();
        } catch (error) {
        }
      }
    };

    const draw = () => {
      if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
        // Update canvas dimensions if video dimensions changed
        if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(video, 0, -40, canvas.width, canvas.height);
        
        // Chroma key effect
        try {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          const threshold = 100;
          const chromaR = 255, chromaG = 0, chromaB = 255;
          
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2];
            if (Math.abs(r - chromaR) < threshold && Math.abs(g - chromaG) < threshold && Math.abs(b - chromaB) < threshold) {
              data[i + 3] = 0;
            }
          }
          ctx.putImageData(imageData, 0, 0);
        } catch (error) {
          // Handle cross-origin or other canvas errors gracefully
        }
      }
      animationFrameId = requestAnimationFrame(draw);
    };

    playVideo();
    draw();
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [currentIndex, filteredCocktails, editingCocktail, currentCocktail.videoFile, videoPreviewUrl]);

  // Handle video source changes - ONLY use Cloudinary URLs, NO FALLBACK
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Determine video URL - ONLY Cloudinary URLs, no local paths
    let videoSrc = '';
    
    if (isCloudinaryUrl(videoPreviewUrl)) {
      videoSrc = videoPreviewUrl;
    } else if (isCloudinaryUrl(currentCocktail.cloudinaryVideoUrl)) {
      videoSrc = currentCocktail.cloudinaryVideoUrl;
    } else if (isCloudinaryUrl(currentCocktail.videoUrl)) {
      videoSrc = currentCocktail.videoUrl;
    }
    
    if (videoSrc && video.src !== videoSrc && !video.src.endsWith(videoSrc)) {
      video.src = videoSrc;
      video.load(); // Force reload
      video.play().catch(err => {
        // NO FALLBACK - Only Cloudinary URLs
        console.error('❌ [MenuManager] Video failed to load from Cloudinary:', err);
        console.error('   Video URL was:', videoSrc);
        // Hide video if it fails
        if (video) {
          video.style.display = 'none';
        }
      });
    } else if (!videoSrc) {
      video.src = '';
      video.style.display = 'none'; // Hide if no Cloudinary URL
    }
  }, [videoPreviewUrl, currentCocktail.videoUrl, currentCocktail.cloudinaryVideoUrl, currentCocktail.videoFile, currentCocktail.itemNumber, API_BASE_URL]);

  // Debug: Log when currentCocktail changes (especially Cloudinary URLs)
  useEffect(() => {
    console.log('[MenuManager] currentCocktail changed:', {
      itemNumber: currentCocktail?.itemNumber,
      name: currentCocktail?.name,
      cloudinaryVideoUrl: currentCocktail?.cloudinaryVideoUrl,
      videoUrl: currentCocktail?.videoUrl,
      isEditing: !!editingCocktail,
      editingItemNumber: editingCocktail?.itemNumber
    });
  }, [currentCocktail?.itemNumber, currentCocktail?.cloudinaryVideoUrl, currentCocktail?.videoUrl, editingCocktail?.itemNumber]);

  // Sync flipped video with main video

  // Navigation functions

  // Staged navigation — guarantees fields never show data from a different cocktail.
  // Stage 1 (sync):  name only — user sees the correct title instantly.
  // Stage 2 (rAF):   video + mapType — checked against name before applying.
  // Stage 3 (rAF):   all remaining fields — checked against name before applying.
  // If the user presses an arrow again before a stage fires, the navVersion guard
  // short-circuits that stage so stale data never reaches the form.
  const navigateToCocktail = useCallback((targetCocktail, targetIndex) => {
    if (!targetCocktail) return;

    // Bump epoch — any in-flight async result that captured an older version is discarded
    const myNavVersion = ++navVersionRef.current;
    isNavigatingRef.current = true;

    // Pre-cancel any in-flight recipe fetch
    recipeRequestIdRef.current += 1;
    activeCocktailIdRef.current = targetCocktail._id || null;

    const myId = targetCocktail._id;

    // === STAGE 0: Recipe — hydrated optimistically when possible ===
    // The menu-manager endpoint already attaches recipe data to each item, so in the common
    // case the recipe can render in the same frame as the name, exactly like the form fields.
    // The authoritative fetch still runs, silently, and refines what's on screen.
    const attachedRecipe = targetCocktail.recipe;
    const attachedMatchesItem = Boolean(attachedRecipe && attachedRecipe._id) &&
      (!attachedRecipe.itemNumber || !targetCocktail.itemNumber ||
        String(attachedRecipe.itemNumber) === String(targetCocktail.itemNumber));

    if (attachedMatchesItem && shouldShowRecipeBuilder(targetCocktail.category)) {
      applyRecipe(
        { ...attachedRecipe, title: targetCocktail.name || attachedRecipe.title },
        myId
      );
      setRecipeLoading(false);
    } else {
      clearRecipe();
      setRecipeLoading(true);  // Show loading immediately so Stage 1 render never flashes "No recipe found"
    }

    setCurrentIndex(targetIndex);

    // === STAGE 1: Name only — renders immediately ===
    setEditingCocktail({
      _id: targetCocktail._id,
      name: targetCocktail.name,
      category: targetCocktail.category,
      itemNumber: targetCocktail.itemNumber || null,
      order: targetCocktail.order || 0,
      status: targetCocktail.status || 'active',
      isActive: targetCocktail.isActive !== false,
      regions: targetCocktail.regions || [], // Include regions early so highlights are correct immediately
      recipe: targetCocktail.recipe || null, // Keep for recipe fetch to find attached recipe
    });

    // === STAGE 2: Video + mapType — after name renders ===
    requestAnimationFrame(() => {
      if (navVersionRef.current !== myNavVersion) return; // Navigated away

      setMapType(inferMapType(targetCocktail));
      setVideoUpload(null);
      setVideoPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      setEditingCocktail(prev => {
        if (!prev || prev._id !== myId) return prev; // ID changed → abort stage
        return {
          ...prev,
          videoFile: targetCocktail.videoFile || '',
          cloudinaryVideoUrl: targetCocktail.cloudinaryVideoUrl || '',
          videoUrl: targetCocktail.videoUrl || '',
          cloudinaryVideoId: targetCocktail.cloudinaryVideoId || '',
          mapType: targetCocktail.mapType || 'world',
        };
      });

      // === STAGE 3: All remaining fields — after video renders ===
      requestAnimationFrame(() => {
        if (navVersionRef.current !== myNavVersion) return; // Navigated away

        setEditingCocktail(prev => {
          if (!prev || prev._id !== myId) return prev; // ID changed → abort stage
          return { ...targetCocktail }; // Full data — ID confirmed correct
        });
      });
    });
  }, [applyRecipe, clearRecipe]);

  const navigateBy = (direction) => {
    if (filteredCocktails.length <= 0) return;
    const computeIndex = (prev) => {
      if (direction === 'next') return (prev + 1) % filteredCocktails.length;
      if (direction === 'prev') return (prev - 1 + filteredCocktails.length) % filteredCocktails.length;
      return prev;
    };
    const nextIndex = computeIndex(currentIndex);
    if (!isNewDraft) {
      const nextCocktail = filteredCocktails[nextIndex];
      if (nextCocktail) {
        navigateToCocktail(nextCocktail, nextIndex);
      } else {
        setCurrentIndex(nextIndex);
      }
    } else {
      setCurrentIndex(nextIndex);
    }
  };

  const requestNavigateBy = (direction) => {
    if (hasUnsavedChanges) {
      setUnsavedChangesModal({ show: true, direction });
      return;
    }
    navigateBy(direction);
  };

  const handleNext = () => { preserveRecipeViewRef.current = recipeViewActive; requestNavigateBy('next'); };
  const handlePrev = () => { preserveRecipeViewRef.current = recipeViewActive; requestNavigateBy('prev'); };

  const showVideoArrows = ['cocktails', 'mocktails', 'spirits', 'wine', 'beer'].includes(
    editorCategory
  ) && filteredCocktails.length > 1;

  // Reset index when category changes
  useEffect(() => {
    setCurrentIndex(0);
    // Ensure we have a valid current cocktail for the new category
    if (filteredCocktails.length > 0 && !filteredCocktails[currentIndex]) {
      setCurrentIndex(0);
    }
  }, [selectedCategory, filteredCocktails.length]);

  const handleSave = async (cocktailData) => {
    console.log('[MenuManager] handleSave called with:', {
      hasData: !!cocktailData,
      name: cocktailData?.name,
      category: cocktailData?.category,
      regions: cocktailData?.regions,
      _id: cocktailData?._id,
      itemNumber: cocktailData?.itemNumber
    });
    
    if (!cocktailData) return;

    // Only require name for all categories - other fields can be added later
    if (!cocktailData.name || !cocktailData.name.trim()) {
      showNotification('Please enter a name for the item.', 'warning');
      return;
    }

    // Determine if this is a new item
    // Must have a valid MongoDB ObjectId (24 hex chars) to be considered existing
    const hasValidId = cocktailData._id && 
                       typeof cocktailData._id === 'string' && 
                       cocktailData._id.length === 24 && 
                       !cocktailData._id.startsWith('new-') &&
                       /^[0-9a-fA-F]{24}$/.test(cocktailData._id);
    const isNew = !hasValidId;

    try {
      setLoading(true);
      const normalizedOrder = Number(cocktailData.order);
      const targetCategory = cocktailData.category || (selectedCategory === 'archived' ? 'cocktails' : selectedCategory);
      // Step 1: Save shared fields to Inventory (source of truth)
      const sheetKey = getSheetKeyFromCategory(targetCategory);
      if (sheetKey) {
        try {
          // Include mapType and current regions from component state in cocktailData for inventory sync
          const cocktailDataWithMapType = { 
            ...cocktailData, 
            mapType,
            regions: selectedRegions // Use current selectedRegions, not stale cocktailData.regions
          };
          console.log('[MenuManager] Saving to inventory - cocktailData.regions (old):', cocktailData.regions);
          console.log('[MenuManager] Saving to inventory - selectedRegions (current):', selectedRegions);
          const sharedFields = extractSharedFieldsForInventory(cocktailDataWithMapType, targetCategory);
          console.log('[MenuManager] Extracted sharedFields for inventory:', sharedFields);
          // For new items, we only need name - other fields can be empty
          if (sharedFields && sharedFields.name) {
            if (isNew) {
              // First, check if an inventory row with this name already exists
              // This prevents duplicates if the first save failed after creating inventory row
              let existingInventoryRow = null;
              try {
                const inventorySheet = await apiCall(`/inventory/${sheetKey}`);
                if (inventorySheet?.rows) {
                  existingInventoryRow = inventorySheet.rows.find(r => 
                    r.values?.name && r.values.name.toLowerCase() === sharedFields.name.toLowerCase()
                  );
                }
              } catch (checkErr) {
                console.warn('Could not check for existing inventory row:', checkErr);
              }
              
              if (existingInventoryRow?.values?.itemNumber) {
                // Use existing inventory row - don't create duplicate
                cocktailData.itemNumber = Number(existingInventoryRow.values.itemNumber);
                console.log('Found existing inventory row with itemNumber:', cocktailData.itemNumber);
              } else {
                // Create new inventory row
                const inventoryResponse = await apiCall(`/inventory/${sheetKey}/rows`, {
                  method: 'POST',
                  body: JSON.stringify({
                    values: sharedFields,
                    updatedBy: 'menumanager'
                  }),
                  headers: {
                    'Content-Type': 'application/json'
                  }
                });
                
                // Get the itemNumber from the created inventory row
                if (inventoryResponse?.sheet?.rows) {
                  const newRow = inventoryResponse.sheet.rows.find(r => 
                    r.values?.name === sharedFields.name
                  );
                  if (newRow?.values?.itemNumber) {
                    cocktailData.itemNumber = Number(newRow.values.itemNumber);
                  }
                }
              }
            } else {
              // Update existing inventory row - single fetch to get both rowId and version
              const itemNumber = cocktailData.itemNumber;
              let inventoryRowId = null;
              let inventoryVersion = null;
              
              console.log('[MenuManager] About to look up inventory row - itemNumber:', itemNumber, 'sheetKey:', sheetKey);
              console.log('[MenuManager] itemNumber check:', itemNumber, 'Number.isFinite:', Number.isFinite(Number(itemNumber)));
              
              if (itemNumber && Number.isFinite(Number(itemNumber))) {
                console.log('[MenuManager] Fetching inventory sheet from:', `/inventory/${sheetKey}`);
                // Single API call to get sheet (includes rows and version)
                const inventoryResponse = await apiCall(`/inventory/${sheetKey}`);
                console.log('[MenuManager] Inventory sheet response:', inventoryResponse);
                const inventorySheet = inventoryResponse?.sheet;
                console.log('[MenuManager] Has rows?', !!inventorySheet?.rows, 'Row count:', inventorySheet?.rows?.length);
                inventoryVersion = inventorySheet?.version;
                if (inventorySheet?.rows) {
                  console.log('[MenuManager] Looking for inventory row with itemNumber:', itemNumber);
                  console.log('[MenuManager] itemNumber type:', typeof itemNumber);
                  console.log('[MenuManager] Number(itemNumber):', Number(itemNumber), 'type:', typeof Number(itemNumber));
                  
                  // Log all inventory rows with their itemNumbers and types
                  inventorySheet.rows.forEach(r => {
                    // Handle values as Map or Object
                    const values = r.values instanceof Map ? Object.fromEntries(r.values) : (r.values || {});
                    console.log(`[MenuManager] Row ${r._id} - name: ${values.name}, itemNumber: ${values.itemNumber}, type: ${typeof values.itemNumber}`);
                    if (values.itemNumber) {
                      console.log(`[MenuManager] - Number(values.itemNumber): ${Number(values.itemNumber)}, type: ${typeof Number(values.itemNumber)}`);
                      console.log(`[MenuManager] - Comparison: ${Number(values.itemNumber)} === ${Number(itemNumber)} = ${Number(values.itemNumber) === Number(itemNumber)}`);
                    }
                  });
                  
                  // Use a more reliable comparison method that handles both string and number types
                  const found = inventorySheet.rows.find(r => {
                    // Handle values as Map or Object
                    const values = r.values instanceof Map ? Object.fromEntries(r.values) : (r.values || {});
                    const rowItemNumber = values.itemNumber;
                    
                    if (!rowItemNumber) return false;
                    
                    // Convert both to strings and compare (most reliable)
                    const match = String(rowItemNumber).trim() === String(itemNumber).trim();
                    
                    console.log(`[MenuManager] Comparing row ${r._id} - itemNumber: ${rowItemNumber} (${typeof rowItemNumber}) with ${itemNumber} (${typeof itemNumber}) = ${match}`);
                    
                    if (match) {
                      console.log('[MenuManager] Found matching inventory row:', r);
                      return true;
                    }
                    return false;
                  });
                  
                  if (found?._id) {
                    inventoryRowId = found._id;
                    console.log('[MenuManager] Using inventory row ID:', inventoryRowId);
                  } else {
                    console.log('[MenuManager] No matching inventory row found!');
                  }
                }
              }
              
              if (!inventoryRowId) {
                console.error('❌ Could not find inventory row by itemNumber for existing cocktail:', {
                  cocktailId: cocktailData._id,
                  cocktailName: cocktailData.name,
                  itemNumber: cocktailData.itemNumber,
                  sheetKey: sheetKey
                });
              }
                
              if (inventoryRowId) {
                const updatedSharedFields = {
                  ...sharedFields,
                  itemNumber: itemNumber,
                  menuManagerId: String(cocktailData._id || '')
                };
                
                console.log('[MenuManager] Updating inventory row with ID:', inventoryRowId);
                console.log('[MenuManager] Updated shared fields:', updatedSharedFields);
                
                try {
                  const updateResponse = await apiCall(`/inventory/${sheetKey}`, {
                    method: 'PATCH',
                    body: JSON.stringify({
                      version: inventoryVersion,
                      rows: [{
                        _id: inventoryRowId,
                        values: updatedSharedFields
                      }],
                      updatedBy: 'menumanager'
                    }),
                    headers: {
                      'Content-Type': 'application/json'
                    }
                  });
                  
                  console.log('[MenuManager] Inventory update response:', updateResponse);
                } catch (updateError) {
                  console.error('[MenuManager] Error updating inventory:', updateError);
                  throw updateError; // Re-throw to be caught by the outer catch
                }
              }
            }
          }
        } catch (inventoryError) {
          console.error('Error saving to inventory:', inventoryError);
          // Don't fail the whole save if inventory save fails, but warn user
          showNotification(`Could not save to inventory: ${inventoryError.message}. Cocktail will still be saved.`, 'warning');
        }
      }
      
      // Step 2: Save MenuManager-only fields to Cocktail model
      const menuManagerOnlyFields = extractMenuManagerOnlyFields(cocktailData);
      
      // Use itemNumber from inventory if we received it, otherwise use existing
      const itemNumberToSave = cocktailData.itemNumber;
      
      // Build form data with itemNumber - backend will use it to generate itemId for filename
      const formData = await buildCocktailFormData({
        ...menuManagerOnlyFields,
        itemNumber: itemNumberToSave, // Include itemNumber from inventory - backend uses this for itemId
        itemId: itemNumberToSave ? `item${itemNumberToSave}` : undefined, // Pass itemId if we have itemNumber
        order: Number.isFinite(normalizedOrder) ? normalizedOrder : 0,
        category: targetCategory,
        status: cocktailData.status, // read only for the display flag; not sent to the API
        name: cocktailData.name, // Name is needed for the form
        regions: cocktailData.regions || selectedRegions || [], // Regions - empty array is fine for new items
        mapType: mapType // Save map type (world or us) with the cocktail
      }, { videoFile: videoUpload });

      // Ensure we never create a duplicate - if we have a valid _id, we MUST update
      if (!isNew && !cocktailData._id) {
        console.error('❌ CRITICAL: Existing cocktail missing _id! Cannot save safely.');
        throw new Error('Cannot save: Existing item is missing its ID. Please refresh and try again.');
      }
      
      // Determine endpoint and method - ensure existing items always use PUT
      let endpoint, method;
      if (hasValidId) {
        // Existing item - MUST use PUT to update, never POST
        endpoint = `/menu-items/${cocktailData._id}`;
        method = 'PUT';
      } else {
        // New item - use POST to create
        endpoint = '/menu-items';
        method = 'POST';
      }

      console.log(`[MenuManager] Saving cocktail to ${endpoint} with method ${method}`);
      const savedCocktail = await apiCall(endpoint, {
        method,
        body: formData
      });
      
      console.log(`[MenuManager] Save API response:`, savedCocktail);
      
      // Save map snapshot (mandatory) - always generate PNG, even with no selections (blank map)
      // Works for all categories except premix: cocktails, mocktails, wine, beer, spirits
      const categoryKey = normalizeCategoryKey(savedCocktail?.category || targetCategory);
      const isPremix = categoryKey === 'premix';
      const finalItemNumber = savedCocktail?.itemNumber || cocktailData.itemNumber;
      
      console.log(`[MenuManager] Save cocktail - category: ${categoryKey}, isPremix: ${isPremix}, itemNumber: ${finalItemNumber}`);
      console.log(`[MenuManager] Regions being saved:`, cocktailData.regions || selectedRegions || []);
      
      // Map snapshot is mandatory for all non-premix items
      if (!isPremix && finalItemNumber && Number.isFinite(finalItemNumber)) {
        try {
          await saveMapSnapshot(finalItemNumber);
        } catch (error) {
          console.error('Error saving map snapshot:', error);
          // Show error to user but don't fail the whole save
          // Extract error message properly (handle Error objects, Event objects, and strings)
          const errorMessage = error instanceof Error 
            ? error.message 
            : typeof error === 'string' 
              ? error 
              : error?.message || String(error);
          showNotification(`Map snapshot could not be saved: ${errorMessage}. The item was saved successfully.`, 'warning');
        }
      } else if (!isPremix) {
        // Warn if we couldn't save map due to missing itemNumber
        console.warn('⚠️ Could not save map snapshot: missing itemNumber');
      }
      
      // Save recipe if it exists and category supports it.
      // Ownership check: never persist a recipe that belongs to a different item — a save
      // racing a navigation must not write one item's ingredients onto another.
      const recipeOwnerAtSave = recipeForCocktailIdRef.current;
      const recipeBelongsToItem = !recipeOwnerAtSave || recipeOwnerAtSave === (cocktailData?._id || null);
      if (recipe && recipeBelongsToItem && shouldShowRecipeBuilder(targetCategory)) {
        try {
          setSavingRecipe(true);
          const recipeType = getRecipeType(targetCategory);
          if (recipeType) {
            // Update recipe title to match cocktail name
            // Sync garnish from MenuManager form to recipe metadata
            const recipeToSave = {
              ...recipe,
              title: cocktailData.name || recipe.title || '',
              type: recipeType,
              metadata: {
                ...recipe.metadata,
                garnish: formValues.garnish || ''
              }
            };

            // Use the already-loaded recipe _id to determine PUT vs POST
            // This avoids an expensive API call to fetch ALL recipes just to check existence
            const existingRecipeId = recipe._id && typeof recipe._id === 'string' 
              && recipe._id.length === 24 && /^[0-9a-fA-F]{24}$/.test(recipe._id)
              ? recipe._id : null;

            const recipeEndpoint = existingRecipeId ? `/recipes/${existingRecipeId}` : '/recipes';
            const recipeMethod = existingRecipeId ? 'PUT' : 'POST';

            // Use FormData if there's a video file, otherwise JSON
            const videoFile = recipeToSave._videoFile;
            let recipeBody;
            if (videoFile instanceof File) {
              const recipeFormData = new FormData();
              recipeFormData.append('title', recipeToSave.title);
              recipeFormData.append('type', recipeToSave.type);
              if (cocktailData.itemNumber) {
                recipeFormData.append('itemNumber', cocktailData.itemNumber);
              }
              recipeFormData.append('metadata', JSON.stringify(recipeToSave.metadata || {}));
              recipeFormData.append('items', JSON.stringify(recipeToSave.items || []));
              recipeFormData.append('totals', JSON.stringify(recipeToSave.totals || { volumeOz: 0, costEach: 0 }));
              recipeFormData.append('batch', JSON.stringify(recipeToSave.batch || {}));
              recipeFormData.append('backgroundColor', recipeToSave.backgroundColor || '#e5e5e5');
              if (recipeToSave.notes) {
                recipeFormData.append('notes', recipeToSave.notes);
              }
              recipeFormData.append('video', videoFile);
              recipeBody = recipeFormData;
            } else {
              // Remove _videoFile from JSON payload (it's not part of the schema)
              const { _videoFile, ...recipeJson } = recipeToSave;
              recipeJson.itemNumber = cocktailData.itemNumber || null;
              recipeBody = recipeJson;
            }

            const savedRecipeResponse = await apiCall(recipeEndpoint, {
              method: recipeMethod,
              body: recipeBody
            });

            // Handle both { recipe: {...} } and direct recipe object responses
            const savedRecipe = savedRecipeResponse?.recipe || savedRecipeResponse;
            // Only adopt the saved recipe if the user hasn't navigated away mid-save
            if (savedRecipe && activeCocktailIdRef.current === recipeOwnerAtSave) {
              // Reset interaction ref BEFORE applyRecipe so the onChange triggered by
              // the new recipe state doesn't falsely mark unsaved changes
              recipeBuilderInteractedRef.current = false;
              applyRecipe(savedRecipe, recipeOwnerAtSave);
            }
          }
        } catch (recipeError) {
          console.error('Error saving recipe:', recipeError);
          // Don't fail the whole save if recipe save fails
          showNotification(`Cocktail saved, but recipe save failed: ${recipeError.message}`, 'warning');
        } finally {
          setSavingRecipe(false);
        }
      }
      
      // Update selectedCategory to match where the item was saved
      // This ensures the new item appears in the list after refresh
      const savedCategory = normalizeCategoryKey(savedCocktail?.category || targetCategory);
      
      if (savedCategory && savedCategory !== 'archived') {
        setSelectedCategory(savedCategory);
      }
      
      // Fetch cocktails to refresh the list first and rehydrate from inventory (source of truth)
      // Small delay to ensure backend has time to update after save
      console.log('[MenuManager] Waiting 500ms for backend to update...');
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('[MenuManager] Fetching cocktails to refresh after save...');
      const refreshedCocktails = await fetchCocktails();
      console.log('[MenuManager] Refreshed cocktails count:', refreshedCocktails?.length || 0);
      
      const refreshedMatch = Array.isArray(refreshedCocktails)
        ? refreshedCocktails.find(c => {
            if (finalItemNumber && Number.isFinite(finalItemNumber)) {
              const match = Number(c.itemNumber) === Number(finalItemNumber);
              if (match) console.log('[MenuManager] Found refreshed match by itemNumber:', finalItemNumber, c);
              return match;
            }
            const match = savedCocktail?._id && c._id === savedCocktail._id;
            if (match) console.log('[MenuManager] Found refreshed match by _id:', savedCocktail._id, c);
            return match;
          })
        : null;
      
      console.log('[MenuManager] refreshedMatch:', refreshedMatch);
      console.log('[MenuManager] savedCocktail:', savedCocktail);
      
      setEditingCocktail(refreshedMatch ? { ...refreshedMatch } : (savedCocktail ? { ...savedCocktail } : null));
      setVideoUpload(null);
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
        setVideoPreviewUrl('');
      }
      setNewCocktail({
        name: '',
        concept: '',
        ingredients: '',
        globalIngredients: '',
        // garnish comes from RecipeBuilder (recipe.metadata.garnish), not from cocktail
        category: savedCategory || selectedCategory,
        videoFile: '',
        order: 0
      });
      
      // Store saved cocktail ID and category in refs for useEffect to find and update index
      // This ensures the video display updates with the saved cocktail's video
      const savedCocktailId = savedCocktail._id || savedCocktail.itemId;
      if (savedCocktailId) {
        savedCocktailIdRef.current = savedCocktailId;
        savedCategoryRef.current = savedCategory || selectedCategory;
      } else {
        // If no ID, reset to 0
        setCurrentIndex(0);
      }
      
      showNotification(`Cocktail ${isNew ? 'created' : 'updated'} successfully!`, 'success');
      setHasUnsavedChanges(false);
      recipeBuilderInteractedRef.current = false;
    } catch (error) {
      console.error('Error saving cocktail:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        stack: error.stack
      });
      showNotification(`Error saving cocktail: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditingCocktail(prev => (prev ? { ...prev, [field]: value } : prev));
    setHasUnsavedChanges(true);
    // Keep name in sync with recipe title while typing
    if (field === 'name') {
      setRecipe(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          title: value
        };
      });
    }
    // Keep garnish in sync with recipe metadata while typing
    if (field === 'garnish') {
      setRecipe(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          metadata: {
            ...(prev.metadata || {}),
            garnish: value
          }
        };
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this cocktail?')) {
      try {
        await apiCall(`/menu-items/${id}`, { method: 'DELETE' });
        fetchCocktails();
      } catch (error) {
        console.error('Error deleting cocktail:', error);
      }
    }
  };

  const handleArchive = async (cocktail) => {
    if (!cocktail?._id) return;
    if (!window.confirm(`Archive ${cocktail.name}? It will be hidden from the site until restored.`)) return;
    try {
      await apiCall(`/menu-items/${cocktail._id}/archive`, { method: 'POST' });
      if (editingCocktail && editingCocktail._id === cocktail._id) {
        setEditingCocktail(null);
      }
      fetchCocktails();
      showNotification(`${cocktail.name} archived. It can be restored from the Archived section.`, 'success');
    } catch (error) {
      console.error('Error archiving cocktail:', error);
      showNotification(`Error archiving item: ${error.message}`, 'error');
    }
  };


  const handleRestore = async (cocktail) => {
    if (!cocktail?._id) return;
    try {
      await apiCall(`/menu-items/${cocktail._id}/restore`, { method: 'POST' });
      fetchCocktails();
      showNotification(`${cocktail.name} restored to the ${cocktail.category?.toUpperCase()} section.`, 'success');
    } catch (error) {
      console.error('Error restoring cocktail:', error);
      showNotification(`Error restoring item: ${error.message}`, 'error');
    }
  };

  const handleRevertChanges = () => {
    if (!editingCocktail) return;
    setHasUnsavedChanges(false);
    if (isNewDraft) {
      setEditingCocktail(prev => prev ? {
        ...prev,
        name: '',
        concept: '',
        ingredients: '',
        globalIngredients: '',
        // garnish comes from RecipeBuilder (recipe.metadata.garnish), not from cocktail
        regions: []
      } : prev);
    } else {
      const fallback = filteredCocktails[currentIndex] || filteredCocktails[0];
      if (fallback) {
        setEditingCocktail({ 
          ...fallback
        });
      }
    }
    setVideoUpload(null);
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
      setVideoPreviewUrl('');
    }
  };

  // Fetch recipe for a cocktail
  // `silent` is set when navigateToCocktail already hydrated this item optimistically: the
  // fetch then refines what's on screen without flipping back to the loading placeholder.
  const fetchRecipeForCocktail = useCallback(async (cocktail, { silent = false } = {}) => {
    const requestId = ++recipeRequestIdRef.current;
    const myNavVersion = navVersionRef.current; // Capture nav version at start of fetch
    const targetCocktailId = cocktail?._id || null;
    const setHydratedRecipe = (nextRecipe) => {
      if (recipeRequestIdRef.current !== requestId) return;
      if (navVersionRef.current !== myNavVersion) return; // User navigated away while fetching
      if (targetCocktailId && activeCocktailIdRef.current && activeCocktailIdRef.current !== targetCocktailId) return;
      lastRecipeHydrateAtRef.current = Date.now();
      recipeBuilderInteractedRef.current = false;
      // Always align the loaded recipe's title with the saved cocktail name.
      // This applies to ALL categories (including premix) so that the name field in
      // MenuManager is always the source of truth on load. The recipe title will only
      // diverge from the cocktail name when the user actively edits it (see onChange guard).
      let recipeToSet = nextRecipe;
      if (recipeToSet && cocktail?.name && recipeToSet.title !== cocktail.name) {
        recipeToSet = { ...recipeToSet, title: cocktail.name };
      }
      applyRecipe(recipeToSet, targetCocktailId);
    };
    // Never downgrade an already-hydrated persisted recipe to a blank one. A silent refine
    // that fails to find anything should leave the optimistic recipe alone rather than wipe it.
    const setBlankRecipe = (recipeType) => {
      if (silent && recipeForCocktailIdRef.current === targetCocktailId && recipeRef.current?._id) return;
      const blankRecipe = createBlankRecipe(recipeType);
      if (cocktail?.itemNumber) {
        blankRecipe.itemNumber = cocktail.itemNumber;
      }
      setHydratedRecipe(blankRecipe);
    };
    if (!cocktail || !cocktail._id || String(cocktail._id).startsWith('new-')) {
      // New item - create blank recipe with itemNumber if available
      const recipeType = getRecipeType(cocktail?.category);
      if (recipeType) {
        setBlankRecipe(recipeType);
      } else {
        setHydratedRecipe(null);
      }
      setRecipeLoading(false); // Early return bypasses try/finally — must clear loading here
      return;
    }

    const recipeType = getRecipeType(cocktail.category);
    if (!recipeType) {
      setHydratedRecipe(null);
      setRecipeLoading(false); // Early return bypasses try/finally — must clear loading here
      return;
    }

    try {
      if (!silent && recipeRequestIdRef.current === requestId) {
        setRecipeLoading(true);
      }

      // NEW: Try to find recipe by itemNumber first (primary method)
      if (cocktail.itemNumber) {
        try {
          const itemNumberResponse = await apiCall(`/recipes?type=${recipeType}&itemNumber=${cocktail.itemNumber}`);
          const itemNumberRecipes = Array.isArray(itemNumberResponse) 
            ? itemNumberResponse 
            : (itemNumberResponse?.recipes || []);
          
          const matchingRecipe = itemNumberRecipes.find(r => String(r.itemNumber) === String(cocktail.itemNumber));
          if (matchingRecipe) {
            setHydratedRecipe(matchingRecipe);
            return;
          }
        } catch (itemNumberError) {
          console.warn('Error fetching recipe by itemNumber:', itemNumberError);
        }
      }
      
      // NAME-BASED FALLBACK: search all recipes of this type and match by title.
      // Recovers orphaned recipes saved before itemNumber was written to the recipe
      // (e.g. early March recipes for Black Forest, Golden Hour, etc.).
      const tryNameFallback = async () => {
        if (!cocktail.name) return false;
        try {
          const allResponse = await apiCall(`/recipes?type=${recipeType}`);
          const allRecipes = Array.isArray(allResponse)
            ? allResponse
            : (allResponse?.recipes || []);
          const nameMatch = allRecipes.find(r =>
            r.title && r.title.toLowerCase().trim() === cocktail.name.toLowerCase().trim()
          );
          if (nameMatch) {
            setHydratedRecipe(nameMatch);
            return true;
          }
        } catch (nameErr) {
          console.warn('Recipe name-fallback lookup failed:', nameErr);
        }
        return false;
      };

      // FALLBACK: The menu-manager endpoint already attaches recipe data to each item.
      // Only use it if the itemNumber matches — prevents a stale/mismatched recipe from
      // a prior cocktail being displayed for the current one.
      if (cocktail.recipe && cocktail.recipe._id) {
        const attachedRecipe = cocktail.recipe;
        const attachedNum = attachedRecipe.itemNumber;
        const cocktailNum = cocktail.itemNumber;
        const numMatch = !attachedNum || !cocktailNum ||
          String(attachedNum) === String(cocktailNum);
        if (numMatch) {
          if (cocktailNum && !attachedNum) attachedRecipe.itemNumber = cocktailNum;
          setHydratedRecipe(attachedRecipe);
        } else {
          // itemNumber mismatch — try name lookup before falling back to blank
          const found = await tryNameFallback();
          if (!found) setBlankRecipe(recipeType);
        }
      } else {
        // No attached recipe — try name lookup before creating blank
        const found = await tryNameFallback();
        if (!found) setBlankRecipe(recipeType);
      }
    } catch (error) {
      console.error('Error fetching recipe:', error);
      const fallbackType = getRecipeType(cocktail.category);
      if (fallbackType) {
        setBlankRecipe(fallbackType);
      } else {
        setHydratedRecipe(null);
      }
    } finally {
      if (recipeRequestIdRef.current === requestId) {
        setRecipeLoading(false);
      }
    }
  }, [apiCall, applyRecipe]); // selectedCategory removed: caused recipe guard to reset on category change, allowing stale recipe data through

  // Update recipe when editingCocktail changes
  useEffect(() => {
    // Update active cocktail ref to prevent stale recipe data from populating fields
    activeCocktailIdRef.current = editingCocktail?._id || null;
    
    // navigateBy already pre-cancels by incrementing recipeRequestIdRef synchronously;
    // fetchRecipeForCocktail does the definitive ++ internally when it starts.
    // A third increment here causes triple-counting that skews the stale-result guard.
    if (editingCocktail && shouldShowRecipeBuilder(editingCocktail.category)) {
      // navigateToCocktail may already have hydrated this exact item from the recipe the
      // menu-manager endpoint attaches. Keep it on screen and refine it silently — no
      // loading flash, no blank frame between arrow presses.
      const alreadyHydrated = Boolean(editingCocktail._id) &&
        recipeForCocktailIdRef.current === editingCocktail._id;
      if (!alreadyHydrated) {
        clearRecipe();
        setRecipeLoading(true);
      }
      fetchRecipeForCocktail(editingCocktail, { silent: alreadyHydrated });
    } else {
      clearRecipe();
      setRecipeLoading(false); // Non-recipe category — clear any loading state
    }
  }, [editingCocktail?._id, editingCocktail?.category, fetchRecipeForCocktail, clearRecipe]);

  // Sync garnish from recipe.metadata.garnish to MenuManager form when recipe loads
  useEffect(() => {
    if (recipe && recipe.metadata?.garnish !== undefined && editingCocktail) {
      // Guard: Only sync when the recipe has been confirmed for THIS item.
      // When recipeForCocktailIdRef is null (cleared on navigation) we must wait — prevents
      // stale recipe from a previous item overwriting the newly-navigated-to item's garnish.
      if (editingCocktail._id && recipeForCocktailIdRef.current !== editingCocktail._id) {
        return;
      }
      if (recipe.itemNumber && editingCocktail.itemNumber && recipe.itemNumber !== editingCocktail.itemNumber) {
        return;
      }
      // Only update if different to avoid infinite loops
      if (editingCocktail.garnish !== recipe.metadata.garnish) {
        setEditingCocktail(prev => ({
          ...prev,
          garnish: recipe.metadata.garnish || ''
        }));
      }
    }
  }, [recipe?.metadata?.garnish, recipe?._id, editingCocktail?.itemNumber, editingCocktail?._id]);

  // Sync name bidirectionally between MenuManager and RecipeBuilder
  // For PRE-MIX, recipe title drives the cocktail name (Title Case)
  useEffect(() => {
    if (recipe && editingCocktail) {
      // Guard: Only sync when the recipe has been confirmed for THIS item.
      // When recipeForCocktailIdRef is null (cleared on navigation) we must wait — prevents
      // stale recipe from a previous item overwriting the newly-navigated-to item's name/title.
      if (editingCocktail._id && recipeForCocktailIdRef.current !== editingCocktail._id) {
        return;
      }
      if (recipe.itemNumber && editingCocktail.itemNumber && recipe.itemNumber !== editingCocktail.itemNumber) {
        return;
      }
      
      const isPremix = normalizeCategoryKey(editingCocktail.category) === 'premix';
      if (isPremix) {
        // For PRE-MIX, recipe title drives the cocktail name — but ONLY during active editing.
        // On load, setHydratedRecipe already aligns recipe.title with cocktail.name so this
        // block is a no-op at that point. Without the guard it would fire on every recipe load.
        if (recipeBuilderInteractedRef.current && recipe.title && recipe.title !== editingCocktail.name) {
          setEditingCocktail(prev => ({
            ...prev,
            name: recipe.title
          }));
        }
      } else {
        // For other categories: cocktail name is the source of truth for the form.
        // Only sync form → recipe (not recipe → form) to prevent an async recipe load
        // from overwriting the name that was just set during navigation.
        if (editingCocktail.name && editingCocktail.name !== recipe.title) {
          setRecipe(prev => {
            if (!prev) return null;
            return { ...prev, title: editingCocktail.name };
          });
        }
      }
    }
  }, [recipe?.title, recipe?._id, editingCocktail?.name, editingCocktail?.category, editingCocktail?.itemNumber]);

  const handleNewItem = () => {
    const blankCocktail = {
      _id: `new-${Date.now()}`,
      name: '',
      concept: '',
      ingredients: '',
      globalIngredients: '',
      garnish: '',
      category: selectedCategory === 'archived' ? 'cocktails' : selectedCategory,
      videoFile: '',
      order: filteredCocktails.length, // Set order to end of list
      isActive: true,
      status: 'active',
      display: false,
      regions: []
    };
    
    // Always set editingCocktail, even if switching categories
    setEditingCocktail(blankCocktail);
    
    // Create blank recipe if category supports it
    const recipeType = getRecipeType(blankCocktail.category);
    if (recipeType) {
      applyRecipe(createBlankRecipe(recipeType), blankCocktail._id);
    } else {
      clearRecipe();
    }
    
    setVideoUpload(null);
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
      setVideoPreviewUrl('');
    }
    setCurrentIndex(0);

    if (videoRef.current) {
      videoRef.current.src = '';
    }
    
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d', { willReadFrequently: true });
      ctx.clearRect(0, 0, canvasRef.current.width || 400, canvasRef.current.height || 300);
    }
  };



  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      {/* Video Options Modal */}
      {videoOptionsModal.show && (
        <div className="delete-modal">
          <div className="delete-modal-content">
            <p className="delete-warning">Video Options</p>
            <p className="delete-question">Choose how to handle this video:</p>
            {isRenderSite && (
              <p className="delete-question" style={{ marginTop: '0.5rem', color: '#dc2626' }}>
                Video processing on local site only
              </p>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.75rem 0',
                marginTop: '0.25rem',
                marginBottom: '0.25rem',
              }}
            >
              <span
                style={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  color: workerStatus?.online ? '#15803d' : '#dc2626',
                }}
              >
                {workerStatus?.online ? 'ONLINE' : 'OFFLINE'}
              </span>
            </div>
            <div className="delete-modal-actions" style={{ flexDirection: 'column', gap: '0.5rem', width: '100%', alignItems: 'stretch' }}>
              <button
                type="button"
                onClick={() => handleVideoOption('process')}
                style={{ 
                  width: '100%', 
                  background: '#f3f4f6', 
                  color: '#374151',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '0.35rem 0.9rem',
                  fontSize: '0.85rem',
                  cursor: !isRenderSite && workerStatus?.online ? 'pointer' : 'not-allowed',
                  opacity: !isRenderSite && workerStatus?.online ? 1 : 0.5
                }}
                disabled={isRenderSite || !workerStatus?.online}
              >
                Process Video
              </button>
              <button
                type="button"
                onClick={() => handleVideoOption('upload')}
                style={{ 
                  width: '100%', 
                  background: '#f3f4f6', 
                  color: '#374151',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '0.35rem 0.9rem',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                Upload Video
              </button>
              <button
                type="button"
                onClick={() => handleVideoOption('upload-icon')}
                style={{ 
                  width: '100%', 
                  background: '#f3f4f6', 
                  color: '#374151',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '0.35rem 0.9rem',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                Upload Icon
              </button>
              <button
                type="button"
                onClick={() => handleVideoOption('cancel')}
                style={{ 
                  width: '100%', 
                  background: '#f3f4f6', 
                  color: '#374151',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '0.35rem 0.9rem',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.show && (
        <div className="delete-modal">
          <div className="delete-modal-content">
            <p className="delete-warning">{notificationModal.title}</p>
            <p className="delete-question">{notificationModal.message}</p>
            <div className="delete-modal-actions" style={{ flexDirection: 'column', gap: '0.5rem', width: '100%', alignItems: 'stretch' }}>
              <button
                type="button"
                onClick={() => setNotificationModal({ show: false, title: '', message: '', type: 'info' })}
                style={{
                  width: '100%',
                  background: notificationModal.type === 'error' ? '#dc2626' : notificationModal.type === 'success' ? '#16a34a' : notificationModal.type === 'warning' ? '#d97706' : '#374151',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '0.35rem 0.9rem',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {unsavedChangesModal.show && (
        <div className="delete-modal">
          <div className="delete-modal-content">
            <p className="delete-warning">Unsaved Changes</p>
            <p className="delete-question">Would you like to save changes?</p>
            <div className="delete-modal-actions" style={{ flexDirection: 'column', gap: '0.5rem', width: '100%', alignItems: 'stretch' }}>
              <button
                type="button"
                onClick={async () => {
                  const dir = unsavedChangesModal.direction;
                  setUnsavedChangesModal({ show: false, direction: null });
                  if (editingCocktail) {
                    await handleSave(editingCocktail);
                  }
                  if (dir) navigateBy(dir);
                }}
                style={{
                  width: '100%',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '0.35rem 0.9rem',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  const dir = unsavedChangesModal.direction;
                  setUnsavedChangesModal({ show: false, direction: null });
                  handleRevertChanges();
                  if (dir) navigateBy(dir);
                }}
                style={{
                  width: '100%',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '0.35rem 0.9rem',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                Discard
              </button>
              <button
                type="button"
                onClick={() => setUnsavedChangesModal({ show: false, direction: null })}
                style={{
                  width: '100%',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '0.35rem 0.9rem',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="menu-manager bg-white min-h-screen px-6 pb-6 w-full" style={{ paddingTop: 0, position: 'relative' }}>
        {/* Unified header — always in document flow above all content */}
        <header ref={headerRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: useNewLayout && headerPadding ? `${headerPadding.left}px` : '100px', paddingRight: useNewLayout && headerPadding ? `${headerPadding.right}px` : '100px', paddingTop: '120px', paddingBottom: '20px' }}>
          {/* VIEW RECIPE / VIEW ITEM toggle + DISPLAY checkbox on left */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {editorCategory !== 'premix' && shouldShowRecipeBuilder(editorCategory) && (
              <button
                onClick={() => setRecipeViewActive(prev => !prev)}
                className={`menu-manager-nav-button px-6 py-3 rounded-lg border transition-all text-lg font-semibold ${
                  recipeViewActive
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {recipeViewActive ? 'VIEW ITEM' : 'VIEW RECIPE'}
              </button>
            )}
            {editingCocktail && (
              <label
                title={isItemArchived ? 'Archived items are always hidden from the website. Restore the item to show it again.' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '12px 24px',
                  cursor: isItemArchived ? 'not-allowed' : 'pointer',
                  userSelect: 'none',
                  opacity: isItemArchived ? 0.5 : 1
                }}
              >
                {/* Archived items are never on the site, so the box reads off and can't be
                    ticked — the section keeps the same header as the live editor either way. */}
                <input
                  type="checkbox"
                  checked={!isItemArchived && editingCocktail.display !== false}
                  disabled={isItemArchived}
                  onChange={(e) => {
                    setEditingCocktail(prev => ({ ...prev, display: e.target.checked }));
                    setHasUnsavedChanges(true);
                  }}
                  style={{ width: '13px', height: '13px', accentColor: '#4b5563', cursor: isItemArchived ? 'not-allowed' : 'pointer' }}
                />
                <span style={{ fontFamily: 'inherit', fontSize: '0.65rem', fontWeight: 400, color: '#4b5563', letterSpacing: '0.05em' }}>DISPLAY</span>
              </label>
            )}
            {isLegacyToggleCategory && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '12px 24px',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <input
                  type="checkbox"
                  checked={legacyView}
                  onChange={(e) => setLegacyView(e.target.checked)}
                  style={{ width: '13px', height: '13px', accentColor: '#4b5563', cursor: 'pointer' }}
                />
                <span style={{ fontFamily: 'inherit', fontSize: '0.65rem', fontWeight: 400, color: '#4b5563', letterSpacing: '0.05em' }}>LEGACY</span>
              </label>
            )}
          </div>
          {/* Category buttons on right */}
          {/* Real flex + gap: the `flex gap-4` classes are inert (no Tailwind in this project). */}
          <div className="flex gap-4" style={{ display: 'flex', gap: '24px' }}>
            {visibleMenuCategories.map((category) => (
              <button
                key={category.key}
                onClick={() => { preserveRecipeViewRef.current = recipeViewActive; setSelectedCategory(category.key); }}
                className={`menu-manager-nav-button px-6 py-3 rounded-lg border transition-all text-lg font-semibold ${
                  selectedCategory === category.key
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </header>

        {/* Content area — flex: 1 so it fills the remaining viewport height below the header;
             justifyContent: center vertically centers all content in that space */}
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: (recipeViewActive || editorCategory === 'premix') ? 'flex-start' : 'center', paddingBottom: (recipeViewActive || editorCategory === 'premix') ? 0 : '40px' }}>

        {/* Recipe Builder for PRE-MIX — same layout as other recipe views */}
        {editingCocktail && normalizeCategoryKey(editingCocktail.category) === 'premix' && (
          <div className="rounded-lg p-6" style={{ position: 'relative', zIndex: 1, backgroundColor: 'transparent', borderColor: 'transparent', border: 'none', paddingTop: '230px' }}>
            {recipePending ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#888', fontSize: '1.1rem' }}>
                Loading recipe…
              </div>
            ) : recipeReady ? (
              <div
                onMouseDown={() => { recipeBuilderInteractedRef.current = true; }}
                onKeyDown={() => { recipeBuilderInteractedRef.current = true; }}
              >
              <RecipeBuilder
                key={`${editingCocktail?._id || ''}`}
                recipe={{
                  ...recipe,
                  title: recipe.title || editingCocktail.name || '', // Use recipe title, fallback to cocktail name
                  metadata: {
                    ...(recipe.metadata || {}),
                    type: recipe.metadata?.type || editingCocktail.ingredients || '' // Sync type from inventory
                  }
                }}
                onChange={((ownerId) => (updatedRecipe) => {
                  // For PRE-MIX, allow title editing and sync it back to cocktail name
                  const accepted = handleRecipeBuilderChange(ownerId, updatedRecipe);
                  if (!accepted) return; // Stale writer from a previously-mounted item
                  // Sync recipe metadata type → editingCocktail.ingredients for inventory sync
                  if (updatedRecipe?.metadata?.type !== undefined) {
                    setEditingCocktail(prev => ({
                      ...prev,
                      ingredients: updatedRecipe.metadata.type || ''
                    }));
                  }
                })(editingCocktail?._id || null)}
                type="premix"
                saving={savingRecipe}
                onSave={async () => {
                  // Save Recipe button - calls handleSave to save the entire cocktail (including recipe)
                  if (editingCocktail) {
                    handleSave(editingCocktail);
                  }
                }}
                onDelete={async () => {
                  // Delete Recipe button - calls handleDelete
                  if (editingCocktail) {
                    handleDelete(editingCocktail._id);
                  }
                }}
                onNewItem={handleNewItem}
                disableTitleEdit={false}
                hideActions={false}
                forceUppercaseTitle={false}
                showOnlyName={true}
              />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#888', fontSize: '1.1rem' }}>
                No recipe found. Save the item first to create a recipe.
              </div>
            )}
          </div>
        )}
        
        {filteredCocktails.length > 0 || editingCocktail ? (
          editorCategory !== 'premix' && !recipeViewActive ? (
            /* Viewer container wrapper with vignette */
            <div style={{ position: 'relative', width: '100%', paddingTop: '62.5%' }}>
              {/* White vignette overlay - around viewer container edges */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(255, 255, 255, 0.5) 60%, rgba(255, 255, 255, 1) 100%)',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              />
              {/* Viewer container - spans from sidebar to right edge, 16:10 aspect ratio */}
              <div
                ref={viewerContainerRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: '#fff',
                  overflow: 'hidden',
                  fontFamily: 'Montserrat, "Helvetica Neue", Helvetica, Arial, sans-serif',
                }}
              >
              {/* Video background fills entire viewer container */}
              {(currentCocktail.videoFile || currentCocktail.itemNumber || currentCocktail.videoUrl || currentCocktail.cloudinaryVideoUrl || videoPreviewUrl) ? (
                <VideoBackground
                  videoSrc={(() => {
                    // Prioritize Cloudinary URLs first, then preview URL (only if Cloudinary), then nothing
                    let finalVideoSrc = '';
                    
                    if (isCloudinaryUrl(currentCocktail.cloudinaryVideoUrl)) {
                      finalVideoSrc = currentCocktail.cloudinaryVideoUrl;
                      console.log('[MenuManager] Video source: Using cloudinaryVideoUrl', finalVideoSrc);
                    } else if (isCloudinaryUrl(currentCocktail.videoUrl)) {
                      finalVideoSrc = currentCocktail.videoUrl;
                      console.log('[MenuManager] Video source: Using videoUrl', finalVideoSrc);
                    } else if (isCloudinaryUrl(videoPreviewUrl)) {
                      // Only use videoPreviewUrl if it's a Cloudinary URL (blob URLs are not valid for final display)
                      finalVideoSrc = videoPreviewUrl;
                      console.log('[MenuManager] Video source: Using videoPreviewUrl', finalVideoSrc);
                    } else {
                      // No valid Cloudinary URL - return empty string so VideoBackground component doesn't render
                      console.log('[MenuManager] Video source: No valid Cloudinary URL found', {
                        hasCloudinaryVideoUrl: !!currentCocktail.cloudinaryVideoUrl,
                        cloudinaryVideoUrl: currentCocktail.cloudinaryVideoUrl,
                        hasVideoUrl: !!currentCocktail.videoUrl,
                        videoUrl: currentCocktail.videoUrl,
                        hasVideoPreviewUrl: !!videoPreviewUrl,
                        videoPreviewUrl: videoPreviewUrl,
                        itemNumber: currentCocktail.itemNumber
                      });
                      finalVideoSrc = '';
                    }
                    
                    return finalVideoSrc;
                  })()}
                  videoRef={videoRef}
                  currentCocktail={currentCocktail}
                  videoPreviewUrl={videoPreviewUrl}
                  API_BASE_URL={API_BASE_URL}
                  isProcessing={!!processingStatus?.active}
                  videoScale={useNewLayout ? 1.2 : 1}
                  videoOffsetX={useNewLayout ? 'calc(100vw / 8)' : null}
                  onLoadedData={() => {
                    if (videoRef.current) {
                      const video = videoRef.current;
                      if (video.paused) {
                        video.play().catch(() => {});
                      }
                    }
                  }}
                />
              ) : (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#6c757d',
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '1.2rem',
                  zIndex: 1,
                }}>
                  No Video Selected
                </div>
              )}

              {/* Processing status overlay - appears in center of viewer behind arrows and form fields */}
              {(() => {
                const currentItem = editingCocktail?.itemNumber || currentCocktail?.itemNumber;
                // Show overlay if processing is active, regardless of itemNumber match
                // This ensures feedback is always visible during any processing
                const shouldShow = !!processingStatus && !!processingStatus.active;
                if (processingStatus && processingStatus.active) {
                  console.log('[MenuManager] Processing status check:', {
                    hasProcessingStatus: !!processingStatus,
                    isActive: processingStatus.active,
                    statusItemNumber: processingStatus.itemNumber,
                    editingItemNumber: editingCocktail?.itemNumber,
                    currentItemNumber: currentCocktail?.itemNumber,
                    shouldShow: shouldShow
                  });
                }
                return shouldShow;
              })() && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.95)',
                    zIndex: 5, // Above video (zIndex: 0) and vignette (zIndex: 1), but below arrows (zIndex: 10) and form fields (zIndex: 25)
                    pointerEvents: 'none', // Don't block interaction with arrows and form fields
                  }}>
                    <div className="loading-spinner" style={{ marginBottom: '1rem', width: '50px', height: '50px' }}></div>
                    <div style={{
                      textAlign: 'center',
                      color: '#333',
                      fontSize: '0.9rem',
                      fontFamily: 'Montserrat, sans-serif',
                      padding: '0 1rem'
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                        {processingStatus.stage === 'creating-job' && 'Creating job...'}
                        {processingStatus.stage === 'uploading-to-worker' && 'Uploading to local worker...'}
                        {processingStatus.stage === 'awaiting-upload' && 'Waiting for upload...'}
                        {processingStatus.stage === 'uploaded' && 'Uploaded. Waiting for processing...'}
                        {processingStatus.stage === 'worker-disconnected' && 'Local worker disconnected'}
                        {processingStatus.stage === 'uploading' && 'Uploading video...'}
                        {processingStatus.stage === 'preprocessing' && 'Cropping and trimming...'}
                        {processingStatus.stage === 'white-balance' && 'Applying white balance...'}
                        {processingStatus.stage === 'icon-generation' && 'Generating icon video...'}
                        {processingStatus.stage === 'extracting' && 'Extracting frames...'}
                        {processingStatus.stage === 'preprocessing-frames' && 'Preprocessing frames...'}
                        {processingStatus.stage === 'processing' && 'Processing video...'}
                        {processingStatus.stage === 'blurring' && 'Blurring background...'}
                        {processingStatus.stage === 'compositing' && 'Compositing video...'}
                        {processingStatus.stage === 'encoding' && 'Encoding final video...'}
                        {processingStatus.stage === 'cloudinary-upload' && 'Uploading to Cloudinary...'}
                        {processingStatus.stage === 'creating-icon' && 'Creating icon version...'}
                        {processingStatus.stage === 'complete' && 'Processing complete!'}
                        {processingStatus.stage === 'initializing' && 'Initializing...'}
                        {!processingStatus.stage && 'Processing...'}
                      </div>
                      <div style={{ color: '#666', fontSize: '0.85rem' }}>
                        {processingStatus.message || ''}
                      </div>
                      {processingStatus.error && (
                        <div style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                          Error: {processingStatus.error}
                        </div>
                      )}
                      {processingStatus.stage && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#888' }}>
                          {(() => {
                            // Calculate overall progress across all stages
                            // Worker stages: preprocessing (crop/trim + frame processing), extracting, compositing, encoding, cloudinary-upload
                            const stageWeights = {
                              'creating-job': { start: 0, weight: 1 },
                              'uploading-to-worker': { start: 1, weight: 4 },
                              'awaiting-upload': { start: 1, weight: 0 },
                              'uploaded': { start: 5, weight: 0 },
                              'preprocessing': { start: 5, weight: 25 },
                              'white-balance': { start: 5, weight: 2 },
                              'icon-generation': { start: 7, weight: 3 },
                              'extracting': { start: 10, weight: 10 },
                              'compositing': { start: 30, weight: 40 },
                              'encoding': { start: 70, weight: 20 },
                              'cloudinary-upload': { start: 90, weight: 10 },
                              'complete': { start: 100, weight: 0 },
                            };
                            const stage = processingStatus.stage;
                            const config = stageWeights[stage] || { start: 0, weight: 0 };
                            const stageProgress = processingStatus.total > 0 
                              ? (processingStatus.progress / processingStatus.total) 
                              : 0;
                            const overallPercent = Math.min(100, Math.floor(config.start + (config.weight * stageProgress)));
                            return `${overallPercent}% complete`;
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
              )}

              {/* LEFT COLUMN - Form Fields (NAME, INGREDIENTS, GARNISH, CONCEPT, PAGE, VIDEO) - positioned absolutely on top */}
              <div
                      style={{ 
                        position: 'absolute',
                        left: 0,
                  top: 0,
                  width: leftColumnWidth,
                        height: '100%',
                  paddingLeft: leftColumnPadding,
                  boxSizing: 'border-box',
                  zIndex: 25,
                  pointerEvents: 'auto',
                    display: 'flex',
                  flexDirection: 'column',
                    justifyContent: 'center',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ marginBottom: '10px' }}>
                    {(!formValues.name || formValues.name.trim() === '') && (
                      <label className="text-lg font-semibold text-gray-800 uppercase tracking-wide block" style={{ marginBottom: '4px' }}>Name</label>
                    )}
                      <input
                        type="text"
                      value={formValues.name || ''}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                        className="w-full px-3 py-2 focus:outline-none"
                        placeholder=""
                        style={{ 
                          fontFamily: 'Montserrat, sans-serif',
                          border: '2px solid #666666',
                          borderRadius: useNewLayout ? '2.5rem' : 0,
                          outline: 'none',
                          background: 'transparent',
                          fontSize: '2rem',
                          fontWeight: 'normal',
                          padding: '1rem',
                          minHeight: '60px',
                          textAlign: 'center',
                          textTransform: 'uppercase'
                        }}
                      disabled={!editingCocktail}
                    />
                  </div>
                  <div>
                    <label className="text-lg font-semibold text-gray-800 uppercase tracking-wide block" style={{ display: 'block', marginBottom: '10px', fontSize: '1.5rem' }}>Ingredients</label>
                      <textarea
                      value={formValues.ingredients || ''}
                      onChange={(e) => handleFieldChange('ingredients', e.target.value)}
                      onFocus={() => setFocusedField('ingredients')}
                      onBlur={() => setFocusedField(null)}
                        className="w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter ingredients"
                        rows={2}
                        style={{ 
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: '1.1rem',
                          border: focusedField === 'ingredients' ? '1px solid #d1d5db' : 'none',
                          outline: 'none',
                          background: 'transparent',
                          overflow: 'hidden',
                          resize: 'none'
                        }}
                      disabled={!editingCocktail}
                    />
                  </div>
                  {shouldShowRecipeBuilder(editorCategory) && editorCategory !== 'premix' && (
                    <div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '17px', marginBottom: '24px' }}>
                        {BASE_SPIRIT_OPTIONS.map((option) => {
                          const selected = Array.isArray(formValues.baseSpirits) && formValues.baseSpirits.includes(option);
                          return (
                            <button
                              key={option}
                              type="button"
                              disabled={!editingCocktail}
                              onClick={() => toggleBaseSpirit(option)}
                              style={{
                                padding: '6px 16px',
                                borderRadius: '2.5rem',
                                // Four shades lighter than the #666666 border grey used by the
                                // other MenuManager fields; selected fills with it, text white.
                                border: '2px solid #aaaaaa',
                                background: selected ? '#aaaaaa' : 'transparent',
                                color: selected ? '#ffffff' : '#aaaaaa',
                                fontFamily: 'Montserrat, sans-serif',
                                fontSize: '0.85rem',
                                letterSpacing: '0.08em',
                                cursor: editingCocktail ? 'pointer' : 'not-allowed',
                                opacity: editingCocktail ? 1 : 0.5,
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {showLegacyFields && (
                  <div>
                    <label className="text-lg font-semibold text-gray-800 uppercase tracking-wide block" style={{ marginBottom: '4px' }}>Concept</label>
                    <textarea
                      value={formValues.concept || ''}
                      onChange={(e) => handleFieldChange('concept', e.target.value)}
                      onFocus={() => setFocusedField('concept')}
                      onBlur={() => setFocusedField(null)}
                      className="w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter cocktail concept"
                      rows={3}
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        border: focusedField === 'concept' ? '1px solid #d1d5db' : 'none',
                        outline: 'none',
                        background: 'transparent',
                        overflow: 'hidden',
                        resize: 'none'
                      }}
                      disabled={!editingCocktail}
                    />
                  </div>
                  )}
                    <input
                      type="file"
                      accept="video/*"
                      ref={videoFileInputRef}
                      onChange={(e) => handleVideoSelection(e.target.files?.[0])}
                      disabled={!editingCocktail}
                      style={{ display: 'none' }}
                    />
                    {isLocalhost && (
                    <button
                      type="button"
                      onClick={() => videoFileInputRef.current?.click()}
                      disabled={!editingCocktail}
                      className={`menu-manager-action-button${useNewLayout ? ' is-rounded' : ''}`}
                      style={{ 
                        background: 'transparent',
                        border: '2px solid #666666',
                        borderColor: '#666666',
                        color: '#666666',
                        padding: '10px 16px',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 500,
                        fontSize: '14px',
                        cursor: editingCocktail ? 'pointer' : 'not-allowed',
                        textTransform: 'uppercase',
                        transition: 'all 0.2s ease',
                        opacity: editingCocktail ? 1 : 0.5,
                        outline: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        appearance: 'none',
                        margin: 0,
                        zIndex: 200,
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.setProperty('color', '#000', 'important');
                          e.currentTarget.style.borderColor = '#000';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }
                      }}
                      onMouseDown={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.setProperty('color', '#000', 'important');
                          e.currentTarget.style.borderColor = '#000';
                        }
                      }}
                      onMouseUp={(e) => {
                        if (!e.currentTarget.disabled && e.currentTarget.matches(':hover')) {
                          e.currentTarget.style.setProperty('color', '#000', 'important');
                          e.currentTarget.style.borderColor = '#000';
                        } else if (!e.currentTarget.disabled) {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }
                      }}
                      onFocus={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }
                      }}
                      onBlur={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }
                      }}
                    >
                      Choose Video File
                    </button>
                    )}
                  </div>
              </div>

              {/* CENTER COLUMN - Arrows positioned at 1/3 height */}
              {showVideoArrows && (
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    // New layout nudges the arrows right 1/8vw and down 48px (smaller
                    // `bottom` == lower on screen); legacy keeps the original centring.
                    transform: useNewLayout ? 'translateX(calc(-50% + 100vw / 8))' : 'translateX(-50%)',
                    bottom: `${(viewerSize?.height ? viewerSize.height / 5 + 200 : 200) - (useNewLayout ? 48 : 0)}px`,
                    width: '256px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 9999,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 32, pointerEvents: 'auto' }}>
                    <button
                      aria-label="Previous"
                      onClick={handlePrev}
                      style={{
                        background: 'transparent',
                        color: '#888',
                        border: 'none',
                        borderRadius: '50%',
                        width: 56,
                        height: 56,
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        margin: 0,
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#222';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#888';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                        <path d="M20 8l-8 8 8 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button
                      aria-label="Next"
                      onClick={handleNext}
                      style={{
                        background: 'transparent',
                        color: '#888',
                        border: 'none',
                        borderRadius: '50%',
                        width: 56,
                        height: 56,
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        margin: 0,
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#222';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#888';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                        <path d="M12 8l8 8-8 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* RIGHT COLUMN - Map, Countries - positioned absolutely on top */}
              {showLegacyFields && (
              <div
                        style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  width: '33.33%',
                  height: '100%',
                  paddingTop: '1rem',
                  paddingRight: '1rem',
                  paddingBottom: '1rem',
                  paddingLeft: 0,
                  boxSizing: 'border-box',
                  zIndex: 10,
                  pointerEvents: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '0',
                }}
              >
              <div style={{ marginBottom: '0', pointerEvents: 'none' }}>
                <MemoizedMapContainer
                  mapSvgContent={mapSvgContent}
                  mapError={mapError}
                  mapRef={mapRef}
                  svgRef={svgRef}
                  onMapReady={handleMapReady}
                  mapType={mapType}
                />
              </div>

              <div style={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', marginTop: '-60px', pointerEvents: 'auto', position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                    <label className="text-lg font-semibold text-gray-800 uppercase tracking-wide block" style={{ marginBottom: '0' }}>
                      {mapType === 'us' ? 'States' : 'Countries'}
                    </label>
                    <select
                      value={mapType}
                      onChange={(e) => {
                        setMapType(e.target.value);
                        setCountryQuery('');
                        setSelectedRegions([]);
                        setHasUnsavedChanges(true);
                      }}
                      style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '0.85rem',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        outline: 'none',
                        marginLeft: '46px'
                      }}
                    >
                      <option value="world">World Map</option>
                      <option value="us">US Map</option>
                    </select>
                  </div>
                  <input
                    type="text"
                    value={countryQuery}
                    onChange={(e) => setCountryQuery(e.target.value)}
                    onFocus={() => setFocusedField('countrySearch')}
                    onBlur={() => setFocusedField(null)}
                    placeholder={mapType === 'us' ? 'Search states...' : 'Search countries...'}
                      className="w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ 
                    fontFamily: 'Montserrat, sans-serif', 
                    marginBottom: '10px',
                    border: focusedField === 'countrySearch' ? '1px solid #d1d5db' : 'none',
                    outline: 'none',
                    background: 'transparent'
                  }}
                  />
                <div style={{ maxHeight: '200px', overflow: 'auto', borderRadius: '8px', padding: '8px' }}>
                  {filteredRegionsList.map((c) => {
                      const selected = selectedRegions.includes(c.code);
                      return (
                      <label
                        key={c.code}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '4px 2px',
                          cursor: 'pointer',
                          fontFamily: 'Montserrat, sans-serif',
                          color: selected ? '#111' : '#555'
                        }}
                      >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleRegion(c.code)}
                            style={{ accentColor: '#d0d0d0' }}
                          />
                        <span>{c.name} ({c.code})</span>
                        </label>
                      );
                    })}
                    {filteredRegionsList.length === 0 && (
                      <div style={{ color: '#888', fontFamily: 'Montserrat, sans-serif' }}>
                        {mapType === 'us' ? 'No states found' : 'No countries found'}
                      </div>
                    )}
                  </div>
                <div style={{ 
                  marginTop: '40px', 
                  color: '#555', 
                  fontFamily: 'Montserrat, sans-serif', 
                  fontSize: '0.9rem',
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  gap: '0.5rem',
                  whiteSpace: 'nowrap',
                  paddingBottom: '4px',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box'
                }}>
                  {selectedCountryDetails.length ? (
                    selectedCountryDetails.map((entry, index) => (
                      <span key={entry.code} style={{ flexShrink: 0, color: '#000' }}>
                        {entry.name} {entry.code}{index < selectedCountryDetails.length - 1 ? ',' : ''}
                      </span>
                    ))
                  ) : (
                    <span style={{ color: '#000' }}>
                      {mapType === 'us' ? 'No states selected' : 'No countries selected'}
                    </span>
                  )}
                  </div>
                  </div>
                </div>
              )}

              {/* White fade at bottom of viewer */}
              <div
                  style={{ 
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '300px',
                  background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 1))',
                  pointerEvents: 'none',
                  zIndex: 15
                }}
              />

              {/* Admin Actions - positioned at bottom of viewer */}
              <div
                      style={{ 
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '2005px',
                  padding: 0,
                  paddingBottom: '60px',
                  margin: 0,
                  boxSizing: 'border-box',
                  zIndex: 30, // Increased from 20 to 30 to be above form fields (zIndex: 25)
                  pointerEvents: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  justifyContent: 'center',
                }}
              >
                <button
                  ref={attachSaveButton}
                  onClick={(e) => {
                    if (editingCocktail) {
                      const button = e.currentTarget;
                      button.style.setProperty('color', '#000', 'important');
                      button.style.setProperty('borderColor', '#000', 'important');
                      handleSave(editingCocktail);
                      setTimeout(() => {
                        // Check if button still exists and is not disabled
                        if (button && !button.disabled) {
                          button.style.setProperty('color', '#666666', 'important');
                          button.style.setProperty('borderColor', '#666666', 'important');
                        }
                      }, 200);
                    }
                  }}
                    disabled={!editingCocktail}
              className={`menu-manager-action-button${useNewLayout ? ' is-rounded' : ''}`}
              style={{ 
                    background: 'transparent',
                    border: '2px solid #666666',
                    padding: '10px 16px',
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    cursor: editingCocktail ? 'pointer' : 'not-allowed',
                    textTransform: 'uppercase',
                    transition: 'all 0.2s ease',
                    opacity: editingCocktail ? 1 : 0.5,
                    outline: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    margin: 0,
                    position: 'relative',
                    zIndex: 31, // Ensure button is above container (zIndex: 30)
                    pointerEvents: 'auto',
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#000', 'important');
                      e.currentTarget.style.borderColor = '#000';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                    }
                  }}
                  onMouseDown={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#000', 'important');
                      e.currentTarget.style.borderColor = '#000';
                    }
                  }}
                  onMouseUp={(e) => {
                    if (!e.currentTarget.disabled && e.currentTarget.matches(':hover')) {
                      e.currentTarget.style.setProperty('color', '#000', 'important');
                      e.currentTarget.style.borderColor = '#000';
                    } else if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                    }
                  }}
                  onFocus={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                }
              }}
            >
              Save Changes
                        </button>
                        <button
              onClick={handleRevertChanges}
              disabled={!editingCocktail}
              className={`menu-manager-action-button${useNewLayout ? ' is-rounded' : ''}`}
              style={{ 
                    background: 'transparent',
                    border: '2px solid #666666',
                    padding: '10px 16px',
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    cursor: editingCocktail ? 'pointer' : 'not-allowed',
                    textTransform: 'uppercase',
                    transition: 'all 0.2s ease',
                    opacity: editingCocktail ? 1 : 0.5,
                    outline: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    margin: 0,
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#000', 'important');
                      e.currentTarget.style.borderColor = '#000';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                    }
                  }}
                  onMouseDown={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#000', 'important');
                      e.currentTarget.style.borderColor = '#000';
                    }
                  }}
                  onMouseUp={(e) => {
                    if (!e.currentTarget.disabled && e.currentTarget.matches(':hover')) {
                      e.currentTarget.style.setProperty('color', '#000', 'important');
                      e.currentTarget.style.borderColor = '#000';
                    } else if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                    }
                  }}
                  onFocus={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                }
              }}
                        >
              Revert Changes
                        </button>
                        <button
              onClick={handleNewItem}
              disabled={selectedCategory === 'archived'}
              className={`menu-manager-action-button${useNewLayout ? ' is-rounded' : ''}`}
              style={{ 
                    background: 'transparent',
                    border: '2px solid #666666',
                    padding: '10px 16px',
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    cursor: selectedCategory !== 'archived' ? 'pointer' : 'not-allowed',
                    textTransform: 'uppercase',
                    transition: 'all 0.2s ease',
                    opacity: selectedCategory !== 'archived' ? 1 : 0.5,
                    outline: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#000', 'important');
                      e.currentTarget.style.borderColor = '#000';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                    }
                  }}
                  onMouseDown={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#000', 'important');
                      e.currentTarget.style.borderColor = '#000';
                    }
                  }}
                  onMouseUp={(e) => {
                    if (!e.currentTarget.disabled && e.currentTarget.matches(':hover')) {
                      e.currentTarget.style.setProperty('color', '#000', 'important');
                      e.currentTarget.style.borderColor = '#000';
                    } else if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                    }
                  }}
                  onFocus={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.setProperty('color', '#666666', 'important');
                      e.currentTarget.style.borderColor = '#666666';
                }
              }}
            >
              {(() => {
                const cat = normalizeCategoryKey(selectedCategory);
                if (cat === 'cocktails') return 'NEW COCKTAIL';
                if (cat === 'mocktails') return 'NEW MOCKTAIL';
                if (cat === 'beer') return 'NEW BEER';
                if (cat === 'wine') return 'NEW WINE';
                if (cat === 'spirits') return 'NEW SPIRIT';
                if (cat === 'premix') return 'NEW PRE-MIX';
                return 'NEW ITEM';
              })()}
                        </button>
            {editingCocktail && !isNewDraft && (
              <>
                        <button
                  onClick={() => handleDelete(editingCocktail._id)}
                  className={`menu-manager-action-button${useNewLayout ? ' is-rounded' : ''}`}
                  style={{ 
                          background: 'transparent',
                          border: '2px solid #666666',
                          padding: '10px 16px',
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 500,
                          fontSize: '14px',
                          cursor: 'pointer',
                          textTransform: 'uppercase',
                          transition: 'all 0.2s ease',
                          outline: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          appearance: 'none',
                  }}
                  onMouseEnter={(e) => {
                        e.currentTarget.style.setProperty('color', '#000', 'important');
                        e.currentTarget.style.borderColor = '#000';
                  }}
                  onMouseLeave={(e) => {
                        e.currentTarget.style.setProperty('color', '#666666', 'important');
                        e.currentTarget.style.borderColor = '#666666';
                      }}
                      onMouseDown={(e) => {
                        e.currentTarget.style.setProperty('color', '#000', 'important');
                        e.currentTarget.style.borderColor = '#000';
                      }}
                      onMouseUp={(e) => {
                        if (e.currentTarget.matches(':hover')) {
                          e.currentTarget.style.setProperty('color', '#000', 'important');
                          e.currentTarget.style.borderColor = '#000';
                        } else {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.setProperty('color', '#666666', 'important');
                        e.currentTarget.style.borderColor = '#666666';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.setProperty('color', '#666666', 'important');
                        e.currentTarget.style.borderColor = '#666666';
                  }}
                        >
                  {(() => {
                    const cat = normalizeCategoryKey(editingCocktail?.category || selectedCategory);
                    if (cat === 'cocktails') return 'DELETE COCKTAIL';
                    if (cat === 'mocktails') return 'DELETE MOCKTAIL';
                    if (cat === 'beer') return 'DELETE BEER';
                    if (cat === 'wine') return 'DELETE WINE';
                    if (cat === 'spirits') return 'DELETE SPIRIT';
                    if (cat === 'premix') return 'DELETE PRE-MIX';
                    return 'DELETE ITEM';
                  })()}
                        </button>
                {canArchive && (
                        <button
                    onClick={() => handleArchive(editingCocktail)}
                    className={`menu-manager-action-button${useNewLayout ? ' is-rounded' : ''}`}
                    style={{ 
                          background: 'transparent',
                          border: '2px solid #666666',
                          padding: '10px 16px',
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 500,
                          fontSize: '14px',
                          cursor: 'pointer',
                          textTransform: 'uppercase',
                          transition: 'all 0.2s ease',
                          outline: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          appearance: 'none',
                    }}
                    onMouseEnter={(e) => {
                          e.currentTarget.style.setProperty('color', '#000', 'important');
                          e.currentTarget.style.borderColor = '#000';
                    }}
                    onMouseLeave={(e) => {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.setProperty('color', '#000', 'important');
                          e.currentTarget.style.borderColor = '#000';
                        }}
                        onMouseUp={(e) => {
                          if (e.currentTarget.matches(':hover')) {
                            e.currentTarget.style.setProperty('color', '#000', 'important');
                            e.currentTarget.style.borderColor = '#000';
                          } else {
                            e.currentTarget.style.setProperty('color', '#666666', 'important');
                            e.currentTarget.style.borderColor = '#666666';
                          }
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                    }}
                        >
                    ARCHIVE
                        </button>
                )}
                {canRestore && (
            <button 
                    onClick={() => handleRestore(editingCocktail)}
                    className={`menu-manager-action-button${useNewLayout ? ' is-rounded' : ''}`}
                    style={{ 
                          background: 'transparent',
                          border: '2px solid #666666',
                          padding: '10px 16px',
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 500,
                          fontSize: '14px',
                          cursor: 'pointer',
                          textTransform: 'uppercase',
                          transition: 'all 0.2s ease',
                          outline: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          appearance: 'none',
                    }}
                    onMouseEnter={(e) => {
                          e.currentTarget.style.setProperty('color', '#000', 'important');
                          e.currentTarget.style.borderColor = '#000';
                    }}
                    onMouseLeave={(e) => {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.setProperty('color', '#000', 'important');
                          e.currentTarget.style.borderColor = '#000';
                        }}
                        onMouseUp={(e) => {
                          if (e.currentTarget.matches(':hover')) {
                            e.currentTarget.style.setProperty('color', '#000', 'important');
                            e.currentTarget.style.borderColor = '#000';
                          } else {
                            e.currentTarget.style.setProperty('color', '#666666', 'important');
                            e.currentTarget.style.borderColor = '#666666';
                          }
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.setProperty('color', '#666666', 'important');
                          e.currentTarget.style.borderColor = '#666666';
                        }}
                      >
                        RESTORE
            </button>
        )}
              </>
            )}
          </div>
        </div>
          </div>
          ) : (
            /* PREMIX layout - keep original structure for now */
              <div>
              {/* Premix content would go here */}
                    </div>
          )
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200" style={{ paddingTop: '460px' }}>
            <p className="text-gray-500 text-lg mb-4">No items found in {menuCategories.find(c => c.key === selectedCategory)?.label}.</p>
            <button 
              onClick={handleNewItem}
              disabled={selectedCategory === 'archived'}
              className={`menu-manager-action-button${useNewLayout ? ' is-rounded' : ''}`}
              style={{ 
                background: 'transparent',
                border: '2px solid #666666',
                color: '#666666',
                padding: '10px 16px',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                cursor: selectedCategory !== 'archived' ? 'pointer' : 'not-allowed',
                textTransform: 'uppercase',
                transition: 'all 0.2s ease',
                opacity: selectedCategory !== 'archived' ? 1 : 0.5,
                outline: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                appearance: 'none',
                margin: 0,
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.setProperty('color', '#000', 'important');
                  e.currentTarget.style.borderColor = '#000';
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.setProperty('color', '#666666', 'important');
                  e.currentTarget.style.borderColor = '#666666';
                }
              }}
              onMouseDown={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.setProperty('color', '#000', 'important');
                  e.currentTarget.style.borderColor = '#000';
                }
              }}
              onMouseUp={(e) => {
                if (!e.currentTarget.disabled && e.currentTarget.matches(':hover')) {
                  e.currentTarget.style.setProperty('color', '#000', 'important');
                  e.currentTarget.style.borderColor = '#000';
                } else if (!e.currentTarget.disabled) {
                  e.currentTarget.style.setProperty('color', '#666666', 'important');
                  e.currentTarget.style.borderColor = '#666666';
                }
              }}
              onFocus={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.setProperty('color', '#666666', 'important');
                  e.currentTarget.style.borderColor = '#666666';
                }
              }}
              onBlur={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.setProperty('color', '#666666', 'important');
                  e.currentTarget.style.borderColor = '#666666';
                }
              }}
            >
              ADD FIRST ITEM
            </button>
          </div>
        )}

        {/* Recipe View - Cocktails / Mocktails only.
            Mounted ONLY when the user clicks VIEW RECIPE, eliminating the loading
            competition between the item form and the recipe builder. */}
        {recipeViewActive && editingCocktail && shouldShowRecipeBuilder(editingCocktail.category) && normalizeCategoryKey(editingCocktail.category) !== 'premix' && (
          <div className="rounded-lg p-6" style={{ position: 'relative', zIndex: 1, backgroundColor: 'transparent', borderColor: 'transparent', border: 'none', paddingTop: '230px' }}>
            {recipePending ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#888', fontSize: '1.1rem' }}>
                Loading recipe…
              </div>
            ) : recipeReady ? (
              <div
                onMouseDown={() => { recipeBuilderInteractedRef.current = true; }}
                onKeyDown={() => { recipeBuilderInteractedRef.current = true; }}
              >
                <RecipeBuilder
                  key={`${editingCocktail?._id || ''}`}
                  recipe={{
                    ...recipe,
                    title: editingCocktail.name || recipe.title
                  }}
                  onChange={((ownerId) => (updatedRecipe) => {
                    handleRecipeBuilderChange(ownerId, updatedRecipe);
                  })(editingCocktail?._id || null)}
                  type={getRecipeType(editingCocktail.category)}
                  saving={savingRecipe}
                  onSave={async () => {
                    if (editingCocktail) {
                      handleSave(editingCocktail);
                    }
                  }}
                  onDelete={null}
                  onNewItem={handleNewItem}
                  disableTitleEdit={false}
                  hideActions={false}
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#888', fontSize: '1.1rem' }}>
                No recipe found. Save the item first to create a recipe.
              </div>
            )}
          </div>
        )}

        {/* Recipe view footer — mirrors header size/position, holds prev/next arrows */}
        {(recipeViewActive || editorCategory === 'premix') && filteredCocktails.length > 1 && (
          <footer style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 100, height: '160px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 32 }}>
            <button
              aria-label="Previous"
              onClick={handlePrev}
              style={{ background: 'transparent', color: '#888', border: 'none', borderRadius: '50%', width: 56, height: 56, fontWeight: 700, cursor: 'pointer', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, margin: 0, transition: 'all 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#222'; e.currentTarget.style.transform = 'scale(1.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#888'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                <path d="M20 8l-8 8 8 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              aria-label="Next"
              onClick={handleNext}
              style={{ background: 'transparent', color: '#888', border: 'none', borderRadius: '50%', width: 56, height: 56, fontWeight: 700, cursor: 'pointer', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, margin: 0, transition: 'all 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#222'; e.currentTarget.style.transform = 'scale(1.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#888'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                <path d="M12 8l8 8-8 8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </footer>
        )}


        </div>{/* end content centering wrapper */}

    </div>
    </>
  );
};

export default MenuManager;
